/**
 * The shared language picker (SE-6508). One implementation now serves the AI
 * summary and the scout-report translation sheet, so one set of anchors serves
 * both — a spec no longer has to know which of the three former pickers it is
 * looking at.
 */
export const TEST_ID_LANGUAGE_SELECTOR = {
  /** The default icon + current-language trigger. */
  TRIGGER: "LANGUAGE_SELECTOR.TRIGGER",
  /** The bottom-sheet surface rendered on small screens. */
  SHEET: "LANGUAGE_SELECTOR.SHEET",
  /** The centered dialog surface rendered on large screens. */
  DIALOG: "LANGUAGE_SELECTOR.DIALOG",
  /** One locale row, keyed by its locale code. */
  optionFor: (locale: string): string => `LANGUAGE_SELECTOR.OPTION.${locale}`,
  /** The optional "save as my default language" row (SE-6511). */
  SAVE_AS_DEFAULT: "LANGUAGE_SELECTOR.SAVE_AS_DEFAULT",
};

export const TEST_ID_CONFIRM_CODE_INPUT_CODE = "CONFIRM_CODE.INPUT_CODE";

/**
 * The inline rejection region on the Enter Code screen (SE-6887).
 *
 * This is the DURABLE signal that a submitted one-time code was rejected. It
 * is deliberately structural: the region exists whenever the confirm-sign-in
 * outcome is a failure, whatever copy the backend supplied — including copy
 * nobody has seen yet. Nothing here matches on the sentence inside it, which
 * the frontend neither authors nor controls: the "You have N attempts left"
 * wording comes from one backend branch, an invalid/expired session from
 * another, and a thrown network error from neither.
 *
 * Assert THIS, never the text it wraps. The predecessor assertion matched
 * `.*attempts left.*` against a transient toast and was never once captured in
 * a view hierarchy across ~120 flow captures — "absent" and "missed" were
 * indistinguishable, which is what a transient signal costs you.
 */
export const TEST_ID_CONFIRM_CODE_ERROR = "CONFIRM_CODE.ERROR";
/**
 * The text node inside {@link TEST_ID_CONFIRM_CODE_ERROR}, carrying the
 * message. Separate from the region id because a Text node is the element that
 * reliably surfaces in BOTH native accessibility trees (see the SE-6812 note
 * on TEST_ID_SIGNIN_DIAL_CODE_SEARCH for a container id that did not).
 */
export const TEST_ID_CONFIRM_CODE_ERROR_TEXT = "CONFIRM_CODE.ERROR_TEXT";

export const TEST_ID_SIGNIN_INPUT_PHONE = "SIGNIN.INPUT_PHONE";
export const TEST_ID_SIGNIN_BUTTON_NEXT = "SIGNIN.BUTTON_NEXT";
/**
 * The DURABLE signal that a sign-in attempt failed — the first half of the
 * journey whose second half is {@link TEST_ID_CONFIRM_CODE_ERROR} (SE-6889).
 *
 * Before this existed the only report of a failed sign-in was a toast that
 * `useToastNotification` rendered GREEN, because the call site omitted
 * `action` and the hook defaults it to `"success"`. It then vanished after
 * 1500ms. A user who is told briefly, in green, that nothing went wrong
 * presses the button again — which is how the reported retry ladder was
 * exhausted.
 *
 * Structural for the same reason as its confirm-code counterpart: the region
 * exists whenever the sign-in outcome is a failure, whatever copy the backend
 * supplied. Assert THIS, not the sentence inside it.
 */
export const TEST_ID_SIGNIN_ERROR = "SIGNIN.ERROR";
/**
 * The text node inside {@link TEST_ID_SIGNIN_ERROR}, carrying the message.
 * Separate from the region id because a Text node is the element that reliably
 * surfaces in BOTH native accessibility trees — see the SE-6812 note on
 * TEST_ID_SIGNIN_DIAL_CODE_SEARCH for a container id that did not.
 */
export const TEST_ID_SIGNIN_ERROR_TEXT = "SIGNIN.ERROR_TEXT";
/**
 * Search field inside the country-dial-code sheet.
 *
 * NOTE (SE-6812): this id does NOT surface in the iOS accessibility tree —
 * verified against a rebuilt binary, where asserting it still failed. Passing
 * `testID` through the Input atom's `field` prop works elsewhere
 * (SIGNIN.INPUT_PHONE reads back fine), but not for this one inside the
 * Select portal. The native flow therefore anchors on a country ROW instead;
 * do not re-point it here expecting the id to appear. Kept because it is still
 * valid identity for web and costs nothing at runtime.
 */
export const TEST_ID_SIGNIN_DIAL_CODE_SEARCH = "SIGNIN.DIAL_CODE_SEARCH";
export const TEST_ID_MENU_HEADER_BUTTON = "MENU_HEADER.OPEN_BUTTON";
export const TEST_ID_GLOBAL_FAB_BUTTON = "global-expandable-fab";

export const TEST_ID_TAB_BAR = {
  HOME: "TAB_BAR.HOME",
  SCOUTING: "TAB_BAR.SCOUTING",
  SQUAD: "TAB_BAR.SQUAD",
  ASK_GEMINI: "TAB_BAR.ASK_GEMINI",
  SEARCH: "TAB_BAR.SEARCH",
  COMPLIANCE: "TAB_BAR.COMPLIANCE",
};

export const TEST_ID_INVITATION = {
  CREATE_BUTTON: "INVITATION.CREATE_BUTTON",
  PHONE_INPUT: "INVITATION.PHONE_INPUT",
  DELETE_ITEM_BUTTON: "INVITATION.DELETE_ITEM_BUTTON",
  SEND_INVITATION_BUTTON: "INVITATION.SEND_INVITATION_BUTTON",
};

export const TEST_ID_RECOMMENDATION = {
  PLAYER_UNCHECKED_ICON: "RECOMMENDATION.PLAYER_UNCHECKED_ICON",
  PLAYER_CHECKED_ICON: "RECOMMENDATION.PLAYER_CHECKED_ICON",
  PLAYER_ITEM: "RECOMMENDATION.PLAYER_ITEM",
  FILTER_BUTTON: "RECOMMENDATION.FILTER_BUTTON",
  ADD_TO_LIST_BUTTON: "RECOMMENDATION.ADD_TO_LIST_BUTTON",
};

export const TEST_ID_WATCHLIST = {
  CHECKBOX_UNCHECKED: "WATCHLIST.CHECKBOX_UNCHECKED",
  CHECKBOX_CHECKED: "WATCHLIST.CHECKBOX_CHECKED",
  POPOVER_OPEN_BUTTON: "WATCHLIST.POPOVER_OPEN_BUTTON",
  NEW_LIST_BUTTON: "WATCHLIST.NEW_LIST_BUTTON",
  EDIT_BUTTON: "WATCHLIST.EDIT_BUTTON",
  SORT_POPOVER_BUTTON: "WATCHLIST.SORT_POPOVER_BUTTON",
  PLAYER_BADGE_VALUE: "WATCHLIST.PLAYER_BADGE_VALUE",
  DELETE_ICON: "WATCHLIST.DELETE_ICON",
  OPEN_FILTER_OPTION: "WATCHLIST.OPEN_FILTER_OPTION",
  FILTER_ROLE_OPTION: "WATCHLIST.FILTER_ROLE_OPTION",
  FILTER_SEARCH_BUTTON: "WATCHLIST.FILTER_SEARCH_BUTTON",
  MY_ROSTER_PLAYER_ITEM: "WATCHLIST.MY_ROSTER_PLAYER_ITEM",
  MOBILE_COMPARE_BUTTON: "WATCHLIST.MOBILE_COMPARE_BUTTON",
  MOBILE_SIMILAR_BUTTON: "WATCHLIST.MOBILE_SIMILAR_BUTTON",
  FILTER_BADGE: "WATCHLIST.FILTER_BADGE",
  SEARCH_BAR: "WATCHLIST.SEARCH_BAR",
};

export const TEST_ID_PLAYER_ROSTER = {
  TEAM_SELECTION_CHECKBOX_UNCHECKED:
    "PLAYER_ROSTER.TEAM_SELECTION_CHECKBOX_UNCHECKED",
  TEAM_SELECTION_CHECKBOX_CHECKED:
    "PLAYER_ROSTER.TEAM_SELECTION_CHECKBOX_CHECKED",
  LEAGUE_SELECTION_CHECKBOX_UNCHECKED:
    "PLAYER_ROSTER.LEAGUE_SELECTION_CHECKBOX_UNCHECKED",
  LEAGUE_SELECTION_CHECKBOX_CHECKED:
    "PLAYER_ROSTER.LEAGUE_SELECTION_CHECKBOX_CHECKED",
};

// SE-7396 — the metric explanation modal opened from a player metric. On iOS
// it renders inside RN's `Modal`; these anchors let native automation assert
// the header sits below the status bar and that the close button responds.
export const TEST_ID_METRIC_EXPLANATION_MODAL = {
  HEADER: "metric-explanation-modal-header",
  CLOSE: "metric-explanation-modal-close",
};

export const TEST_ID_PLAYER_DETAIL = {
  SIMILAR_PLAYER_BUTTON: "PLAYER_DETAIL.SIMILAR_PLAYER_BUTTON",
  COMPARE_BUTTON: "PLAYER_DETAIL.COMPARE_BUTTON",
  ADD_TO_LIST_BUTTON: "PLAYER_DETAIL.ADD_TO_LIST_BUTTON",
  ADD_NEW_NOTE_BUTTON: "PLAYER_DETAIL.ADD_NEW_NOTE_BUTTON",
  REMOVE_NOTE_BUTTON: "PLAYER_DETAIL.REMOVE_NOTE_BUTTON",
  EDIT_NOTE_BUTTON: "PLAYER_DETAIL.EDIT_NOTE_BUTTON",
  // SE-5519 — note add/edit modal (replaces the inline compose/edit forms)
  NOTE_EDITOR_MODAL: "note-editor-modal",
  NOTE_EDITOR_INPUT: "note-editor-input",
  NOTE_EDITOR_VISIBILITY_SWITCH: "note-editor-visibility-switch",
  NOTE_EDITOR_SAVE_BUTTON: "note-editor-save-btn",
  NOTE_EDITOR_CANCEL_BUTTON: "note-editor-cancel-btn",
  CLOSE_BUTTON: "PLAYER_DETAIL.CLOSE_BUTTON",
  // SE-6876 — the two tabs the strip previously left without a testID, so a
  // spec asserting "every tab opens" can address all five by id rather than by
  // label text.
  OVERVIEW_TAB: "PLAYER_DETAIL.OVERVIEW_TAB",
  DATA_HUB_TAB: "PLAYER_DETAIL.DATA_HUB_TAB",
  REPORTS_NOTES_TAB: "PLAYER_DETAIL.REPORTS_NOTES_TAB",
  SIMILAR_PLAYERS_TAB: "PLAYER_DETAIL.SIMILAR_PLAYERS_TAB",
  FINANCIALS_TAB: "PLAYER_DETAIL.FINANCIALS_TAB",
  FINANCIALS_EMPTY_STATE: "PLAYER_DETAIL.FINANCIALS_EMPTY_STATE",
  FINANCIALS_DOCUMENT_TYPE_SELECT:
    "PLAYER_DETAIL.FINANCIALS_DOCUMENT_TYPE_SELECT",
  FINANCIALS_EDIT_IN_CONTRACTS_BUTTON:
    "PLAYER_DETAIL.FINANCIALS_EDIT_IN_CONTRACTS_BUTTON",
  FINANCIALS_INNER_TAB_CONTRACT: "PLAYER_DETAIL.FINANCIALS_INNER_TAB_CONTRACT",
  FINANCIALS_INNER_TAB_LIVE: "PLAYER_DETAIL.FINANCIALS_INNER_TAB_LIVE",
  SCOUTING_REPORTS_HEADING: "PLAYER_DETAIL.SCOUTING_REPORTS_HEADING",
  // SE-6876: one per RENDERED report row. The heading and the count badge both
  // survive a list that renders zero rows — the badge reads `reportCount`,
  // which is a separate prop from `reports.length` — so neither can carry an
  // assertion about rows actually being on screen.
  SCOUTING_REPORT_ROW: "PLAYER_DETAIL.SCOUTING_REPORT_ROW",
  NOTES_HEADING: "PLAYER_DETAIL.NOTES_HEADING",
  AI_SUMMARY_HEADING: "PLAYER_DETAIL.AI_SUMMARY_HEADING",
  // SE-8547: the AI Scouting Summary "Generate · {language}" trigger. iOS
  // exposes only its accessibility label, never the visible text.
  AI_SUMMARY_GENERATE_TRIGGER: "PLAYER_DETAIL.AI_SUMMARY_GENERATE_TRIGGER",
  TRANSFERS_HEADING: "PLAYER_DETAIL.TRANSFERS_HEADING",
  PLAYER_NAME: "PLAYER_DETAIL.PLAYER_NAME",
  // SE-6291 — Data Hub "Individual Metric Insights" chart legend
  METRIC_INSIGHTS_LEGEND: "PLAYER_DETAIL.METRIC_INSIGHTS_LEGEND",
  METRIC_INSIGHTS_LEGEND_POSITION:
    "PLAYER_DETAIL.METRIC_INSIGHTS_LEGEND_POSITION",
  // SE-5391 — white page surface root wrapper
  ROOT: "PLAYER_DETAIL.ROOT",
  HEADER_POSITIONS: "PLAYER_DETAIL.HEADER_POSITIONS",
  HEADER_CLUB: "PLAYER_DETAIL.HEADER_CLUB",
  // SE-8547: a 1px marker pinned to the BOTTOM edge of the mobile sticky
  // header. Maestro's `below:` compares TOP edges only, so an anchor must have
  // its top on the line that matters: anything whose top is below this marker's
  // is clear of the header. HEADER_CLUB is the first identity row, not the
  // header's bottom, and a control under the second row passed it.
  STICKY_HEADER_BOTTOM: "PLAYER_DETAIL.STICKY_HEADER_BOTTOM",
  HEADER_SKELETON: "PLAYER_DETAIL.HEADER_SKELETON",
  BREADCRUMB: "PLAYER_DETAIL.BREADCRUMB",
  SIMILAR_PLAYERS_LIST: "PLAYER_DETAIL.SIMILAR_PLAYERS_LIST",
  SIMILAR_PLAYER_ROW: "PLAYER_DETAIL.SIMILAR_PLAYER_ROW",
  SIMILAR_PLAYER_ROW_NAME: "PLAYER_DETAIL.SIMILAR_PLAYER_ROW_NAME",
  SP_SORT_DROPDOWN: "PLAYER_DETAIL.SP_SORT_DROPDOWN",
  SP_SORT_OPTION_PREFIX: "PLAYER_DETAIL.SP_SORT_OPTION-",
  SP_COMMITTED_BADGE: "PLAYER_DETAIL.SP_COMMITTED_BADGE",

  // SE-4479 / SE-4480 / SE-4482 / SE-4488 — Player Page charts
  RADAR_CHART: "PLAYER_DETAIL.RADAR_CHART",
  DOT_PLOT: "PLAYER_DETAIL.DOT_PLOT",
  RECENT_PERFORMANCES: "PLAYER_DETAIL.RECENT_PERFORMANCES",
  PERFORMANCE_BAR_PREFIX: "PLAYER_DETAIL.PERFORMANCE_BAR",
  CHART_LEGEND: "PLAYER_DETAIL.CHART_LEGEND",
  AXIS_LABEL_Y: "PLAYER_DETAIL.AXIS_LABEL_Y",
  METRIC_SELECTOR_PREFIX: "PLAYER_DETAIL.METRIC_SELECTOR",
  SELECTED_MATCH_INFO: "PLAYER_DETAIL.SELECTED_MATCH_INFO",

  // SE-5083 — Mobile player FAB (Notion 8.6)
  MOBILE_FAB: "PLAYER_DETAIL.MOBILE_FAB",
  MOBILE_FAB_BACKDROP: "PLAYER_DETAIL.MOBILE_FAB_BACKDROP",
  MOBILE_FAB_ACTION_LIST: "PLAYER_DETAIL.MOBILE_FAB_ACTION_LIST",
  MOBILE_FAB_ACTION_NOTE: "PLAYER_DETAIL.MOBILE_FAB_ACTION_NOTE",
  MOBILE_FAB_ACTION_REPORT: "PLAYER_DETAIL.MOBILE_FAB_ACTION_REPORT",
  // SE-5499 — Add to Pipeline FAB action
  MOBILE_FAB_ACTION_PIPELINE: "PLAYER_DETAIL.MOBILE_FAB_ACTION_PIPELINE",
  // SE-6910 — empty tail block reserving the FAB's footprint at the end of the
  // scroll content, so no row control can come to rest underneath the FAB
  MOBILE_FAB_SAFE_AREA: "PLAYER_DETAIL.MOBILE_FAB_SAFE_AREA",
};

export const TEST_ID_AI_PLAYER_SUMMARY = {
  CONTAINER: "AI_PLAYER_SUMMARY.CONTAINER",
  HEADER: "AI_PLAYER_SUMMARY.HEADER",
  TITLE: "AI_PLAYER_SUMMARY.TITLE",
  TIMESTAMP: "AI_PLAYER_SUMMARY.TIMESTAMP",
  CHEVRON_BUTTON: "AI_PLAYER_SUMMARY.CHEVRON_BUTTON",
  LANGUAGE_SELECTOR: "AI_PLAYER_SUMMARY.LANGUAGE_SELECTOR",
  LANGUAGE_OPTION: "AI_PLAYER_SUMMARY.LANGUAGE_OPTION",
  REFRESH_BUTTON: "AI_PLAYER_SUMMARY.REFRESH_BUTTON",
  CONTENT: "AI_PLAYER_SUMMARY.CONTENT",
  LOADING_INDICATOR: "AI_PLAYER_SUMMARY.LOADING_INDICATOR",
  GENERATING_INDICATOR: "AI_PLAYER_SUMMARY.GENERATING_INDICATOR",
  ERROR_MESSAGE: "AI_PLAYER_SUMMARY.ERROR_MESSAGE",
  EMPTY_STATE: "AI_PLAYER_SUMMARY.EMPTY_STATE",
  TRY_AGAIN_BUTTON: "AI_PLAYER_SUMMARY.TRY_AGAIN_BUTTON",
  GENERATE_BUTTON: "AI_PLAYER_SUMMARY.GENERATE_BUTTON",
};

export const TEST_ID_SIMILAR_PLAYER = {
  PLAYER_RESULT_ITEM: "SIMILAR_PLAYER.PLAYER_RESULT_ITEM",
  ADD_TO_LIST_BUTTON: "SIMILAR_PLAYER.ADD_TO_LIST_BUTTON",
  ACCORDION_TRIGGER: "SIMILAR_PLAYER.ACCORDION_TRIGGER",
  LEAGUES_DROPDOWN_ITEM: "SIMILAR_PLAYER.LEAGUES_DROPDOWN_ITEM",
  LEAGUES_DROPDOWN_ITEM_SELECTED:
    "SIMILAR_PLAYER.LEAGUES_DROPDOWN_ITEM_SELECTED",
  LEAGUES_DROPDOWN_ITEM_UNSELECTED:
    "SIMILAR_PLAYER.LEAGUES_DROPDOWN_ITEM_UNSELECTED",
  MULTISELECT_SEARCH_INPUT: "SIMILAR_PLAYER.MULTISELECT_SEARCH_INPUT",
};

export const TEST_ID_COMPARE_PLAYER = {
  COMPOSITE_METRIC_ITEM_VALUE: "COMPARE_PLAYER.COMPOSITE_METRIC_ITEM_VALUE",
  MY_ROSTER_PLAYER_ITEM: "COMPARE_PLAYER.MY_ROSTER_PLAYER_ITEM",
};

export const TEST_ID_GEMINI_CHAT = {
  /**
   * Shared id carried by EVERY suggested-question chip. Fine for counting the
   * chips, never for picking one — a positional query against four identical
   * ids passes even when the wrong chip fires. To address ONE chip, filter this
   * locator by the chip's accessibility label, which is its question text
   * (SE-6900).
   */
  SUGGESTED_QUESTION: "GEMINI_CHAT.SUGGESTED_QUESTION",
  /**
   * The desktop suggested-questions block's width-bearing column. It shares
   * the composer's cap so the two line up (SE-8864); the width-parity e2e
   * compares this element's box with `COMPOSER`'s.
   */
  SUGGESTED_COLUMN: "GEMINI_CHAT.SUGGESTED_COLUMN",
  MESSAGE_PROGRESS: "GEMINI_CHAT.MESSAGE_PROGRESS",
  THREAD_SIDEBAR_TOGGLE: "GEMINI_CHAT.THREAD_SIDEBAR_TOGGLE",
  THREAD_LIST_ITEM: "GEMINI_CHAT.THREAD_LIST_ITEM",
  NEW_THREAD_BUTTON: "GEMINI_CHAT.NEW_THREAD_BUTTON",
  DELETE_THREAD_BUTTON: "GEMINI_CHAT.DELETE_THREAD_BUTTON",
  MESSAGE_BUBBLE: "GEMINI_CHAT.MESSAGE_BUBBLE",
  /**
   * The desktop composer's width-bearing wrapper. Carried so the SE-7153
   * width-parity assertion can address the element that actually holds the
   * cap, rather than the textarea nested two levels inside it.
   */
  COMPOSER: "GEMINI_CHAT.COMPOSER",
  /**
   * The narrow layout's composer, absolutely positioned over the bottom of the
   * message list. SE-8674 measures its top edge: a message is only readable
   * when it ends above it, which `toBeInViewport` cannot tell.
   */
  MOBILE_COMPOSER: "GEMINI_CHAT.MOBILE_COMPOSER",
  USER_MESSAGE: "GEMINI_CHAT.USER_MESSAGE",
  AI_MESSAGE: "GEMINI_CHAT.AI_MESSAGE",
  LOADING_DOTS: "GEMINI_CHAT.LOADING_DOTS",
  PLAYER_LINK: "GEMINI_CHAT.PLAYER_LINK",
};

export const TEST_ID_ORGANIZATION = {
  CREATE_BUTTON: "ORGANIZATION.CREATE_BUTTON",
  EDIT_BUTTON: "ORGANIZATION.EDIT_BUTTON",
  SWITCH_ORGANIZATION_BUTTON: "ORGANIZATION.SWITCH_ORGANIZATION_BUTTON",
};

export const TEST_ID_TRANSFER_ALERT = {
  PLAYER_ITEM: "TRANSFER_ALERT.PLAYER_ITEM",
  EMPTY_STATE: "TRANSFER_ALERT.EMPTY_STATE",
};

export const TEST_ID_DASHBOARD = {
  TRANSFER_ALERTS_HEADING: "DASHBOARD.TRANSFER_ALERTS_HEADING",
  EXPIRING_CONTRACTS_TAB: "DASHBOARD.EXPIRING_CONTRACTS_TAB",
  RECENT_TRANSFERS_TAB: "DASHBOARD.RECENT_TRANSFERS_TAB",
  POSITION_DISTRIBUTION_HEADING: "DASHBOARD.POSITION_DISTRIBUTION_HEADING",
  ROSTER_OVERVIEW_HEADING: "DASHBOARD.ROSTER_OVERVIEW_HEADING",
  RECOMMENDED_PLAYERS_HEADING: "DASHBOARD.RECOMMENDED_PLAYERS_HEADING",
};

/**
 * MR-4.2 (SE-6985) — the submitted match report view.
 *
 * Rows are keyed by EVALUATION id, not player id: a report is one row per
 * evaluation, and keying by player would collide if the same player were ever
 * evaluated twice on one report.
 */
export const TEST_ID_MATCH_REPORT_VIEW = {
  SCREEN: "MATCH_REPORT_VIEW.SCREEN",
  HEADER: "MATCH_REPORT_VIEW.HEADER",
  STATUS: "MATCH_REPORT_VIEW.STATUS",
  SUMMARY: "MATCH_REPORT_VIEW.SUMMARY",
  AI_SUMMARY: "MATCH_REPORT_VIEW.AI_SUMMARY",
  PDF: "MATCH_REPORT_VIEW.PDF",
  SHARE: "MATCH_REPORT_VIEW.SHARE",
  EDIT: "MATCH_REPORT_VIEW.EDIT",
  MISSING: "MATCH_REPORT_VIEW.MISSING",
  summaryRowFor: (evaluationId: string): string =>
    `MATCH_REPORT_VIEW.ROW.${evaluationId}`,
  // AC4 (Jake, 7 Aug): its own identity, because the point of the ruling is
  // that this button is DISTINCT from the "Player report" link beside it.
  playerPageButtonFor: (evaluationId: string): string =>
    `MATCH_REPORT_VIEW.PLAYER_PAGE.${evaluationId}`,
  sortColumnFor: (key: string): string => `MATCH_REPORT_VIEW.SORT.${key}`,
};

export const TEST_ID_SCOUTING = {
  ALL_TARGETS_TAB: "SCOUTING.ALL_TARGETS_TAB",
  BOARDS_TAB: "SCOUTING.BOARDS_TAB",
  // SE-8371 retired the tab; kept so specs can assert it has count 0.
  SHADOW_TEAM_TAB: "SCOUTING.SHADOW_TEAM_TAB",
  SCOUT_REPORTS_TAB: "SCOUTING.SCOUT_REPORTS_TAB",
  // SE-7326 — Fixture Planning as a Scouting tab, replacing the standalone
  // rail item. Gated: absent unless the match-report gate resolved allowed,
  // which is the direction the tests have to assert in BOTH ways.
  FIXTURES_TAB: "SCOUTING.FIXTURES_TAB",
  // SE-8200 — the BETA pill beside the Matches label. Built inline by
  // `TabItemView` as `tab-${tab.id}-beta`, so this mirrors that id rather
  // than following the `SCOUTING.*` convention the tabs themselves use.
  FIXTURES_TAB_BETA: "tab-fixtures-beta",

  // MR-4.1 (SE-6984) — the unified Reports list.
  //
  // Rows are keyed by report id so a spec can assert it expanded the report it
  // MEANT to, rather than "some row opened and players appeared". The two kinds
  // share one key space on purpose: they share one list, and a spec asserting
  // ordering has to be able to name a row without knowing which kind it is.
  REPORT_FILTERS: "SCOUTING.REPORTS.FILTERS",
  REPORTS_LIST: "SCOUTING.REPORTS.LIST",
  NEW_REPORT_MENU: "SCOUTING.REPORTS.NEW_REPORT",
  /**
   * The List / Table segmented toggle in the Reports header (SE-7553).
   *
   * Three ids, not one, because this stopped being a toggle. It used to be a
   * single button whose label flipped, so one id and one `.click()` said
   * everything; it is now two segments of which exactly one is on, and a spec
   * that wants the table has to be able to NAME the table rather than press
   * whatever the button currently is. `REPORTS_VIEW_TOGGLE` addresses the
   * track the two sit in — the geometry regression in SE-7208 is about the
   * whole control not being crushed, not about either segment.
   *
   * Under `VIEW.` rather than beside `REPORTS_LIST` ("SCOUTING.REPORTS.LIST"):
   * that id is the LIST BODY, and a segment sharing its stem would collide
   * with any spec addressing the body by prefix.
   */
  REPORTS_VIEW_TOGGLE: "SCOUTING.REPORTS.VIEW_TOGGLE",
  REPORTS_VIEW_LIST: "SCOUTING.REPORTS.VIEW.LIST",
  REPORTS_VIEW_TABLE: "SCOUTING.REPORTS.VIEW.TABLE",
  REPORTS_FILTER_SHEET_BUTTON: "SCOUTING.REPORTS.FILTER_SHEET_BUTTON",
  /**
   * The Reports search box (SE-8081).
   *
   * ONE id, not one per view. The input used to live inside the dense table's
   * toolbar, which meant the Match view had no search at all and the Player
   * view's query died with the toolbar on every toggle; it is now rendered
   * once by the tab, ABOVE the fork, so a spec that types into "the Reports
   * search" must not have to know which segment happens to be on.
   *
   * It carried no id at all before, so the only handle was its placeholder —
   * copy, which a design pass is free to rewrite.
   */
  REPORTS_SEARCH: "SCOUTING.REPORTS.SEARCH",
  /** The search box's clear affordance, shown only while a query is typed. */
  REPORTS_SEARCH_CLEAR: "SCOUTING.REPORTS.SEARCH_CLEAR",
  reportFilterChipFor: (filter: string): string =>
    `SCOUTING.REPORTS.FILTER.${filter}`,
  reportRowFor: (reportId: string): string =>
    `SCOUTING.REPORTS.ROW.${reportId}`,
  /**
   * The row's count chip (SE-8007).
   *
   * Its own stem rather than `…ROW.<id>.COUNT`: `SCOUTING.REPORTS.ROW.` is
   * matched by prefix in specs that gather rows, and a child sharing it would
   * be counted as one.
   *
   * It exists so the chip's number and the rows it expands to can be asserted
   * as a PAIR in one test. Asserting each half separately is what let the two
   * drift — the chip counted every evaluation row while the expansion rendered
   * only the noted ones, and both halves were individually "correct".
   */
  reportRowCountFor: (reportId: string): string =>
    `SCOUTING.REPORTS.COUNT.${reportId}`,
  /**
   * The row's meta line — competition, date and the attribution (SE-8074).
   *
   * Its own stem, for the reason given on {@link reportRowCountFor}:
   * `SCOUTING.REPORTS.ROW.` is gathered by prefix, so a child under it would
   * be counted as an extra row.
   *
   * **Do not tidy this back to `…ROW.<id>.META`.** It reads like the more
   * organised choice and it is the defect: the specs it would break are ones
   * that never mention the meta line, and they would break by miscounting
   * rather than by failing to find anything — the hardest shape to trace back
   * to a rename.
   *
   * It exists because the thing SE-8074 fixed is only observable as a
   * DIFFERENCE between two rows. Several scouts may each report on one genuine
   * fixture — supported by design — and their rows share a title, a
   * competition and a date, so the attribution is the only part that can tell
   * them apart. Addressing each row's meta line by id is what makes that
   * comparison assertable; a row count is identical before and after the fix,
   * and `toBeVisible` cannot tell an author from a roster.
   */
  reportRowMetaFor: (reportId: string): string =>
    `SCOUTING.REPORTS.META.${reportId}`,
  /**
   * The row's chevron — expand/collapse only (SE-8118).
   *
   * Its own stem, for the reason given on {@link reportRowCountFor}:
   * `SCOUTING.REPORTS.ROW.` is gathered by prefix, so a child under it would be
   * counted as an extra row. Do not tidy this to `…ROW.<id>.EXPAND`.
   *
   * It exists because the row now carries TWO affordances. The body opens the
   * report and the chevron expands it in place, and the only way to assert that
   * split is to press each half and see a different thing happen. Reaching only
   * `reportRowFor` cannot tell a row that navigates from one that expands.
   */
  reportRowExpandFor: (reportId: string): string =>
    `SCOUTING.REPORTS.EXPAND.${reportId}`,
  /**
   * The row's identity disc — the initials avatar (SE-7859).
   *
   * Its own stem, for the reason given on {@link reportRowCountFor}:
   * `SCOUTING.REPORTS.ROW.` is gathered by prefix, so a child under it would be
   * counted as an extra row. Do not tidy this to `…ROW.<id>.AVATAR`.
   *
   * It exists because the disc is now a per-kind decision: a single-player row
   * draws one, a match row draws none. Only an id on the disc itself can assert
   * the ABSENCE on a match row — the row is present either way.
   */
  reportRowAvatarFor: (reportId: string): string =>
    `SCOUTING.REPORTS.AVATAR.${reportId}`,
  reportPlayerRowFor: (evaluationId: string): string =>
    `SCOUTING.REPORTS.PLAYER.${evaluationId}`,
  newReportOptionFor: (option: string): string =>
    `SCOUTING.REPORTS.NEW_REPORT.${option}`,

  // MR-4.1 (SE-7524) — the player picker the two non-match options open.
  //
  // Its own namespace rather than `SCOUTING.REPORTS.NEW_REPORT.*`: that prefix
  // is the menu's option key space, and a spec that addresses options by
  // prefix would start matching the picker too.
  //
  // The search FIELD, not a root: the picker has two hosts and neither owns a
  // testable root of its own — `MobileSheetChrome` hard-codes its own chrome
  // anchors and the desktop modal takes none — so the field is the anchor that
  // exists on both paths. The create affordance is addressed by its own
  // component-owned `manually-add-player-option`.
  PLAYER_PICKER_SEARCH: "SCOUTING.REPORTS.PLAYER_PICKER.SEARCH",
  // Retained as the regression anchor for the SE-4614 "desktop heading must
  // stay removed" test; no element renders this testId anymore.
  HEADING: "SCOUTING.HEADING",
  MOBILE_TARGET_CARD: "SCOUTING.MOBILE_TARGET_CARD",
  // SE-4141: tappable position badge (primary + `+N`) and the sheet it opens.
  MOBILE_TARGET_POSITIONS: "SCOUTING.MOBILE_TARGET_POSITIONS",
  MOBILE_POSITIONS_SHEET: "SCOUTING.MOBILE_POSITIONS_SHEET",
  // SE-4143: avg-scout-rating secondary badge on the mobile card.
  MOBILE_TARGET_AVG_RATING: "SCOUTING.MOBILE_TARGET_AVG_RATING",
};

/** Mobile scouting All-Targets sort sheet controls (SE-4146). */
export const TEST_ID_SCOUTING_SORT = {
  /** Toolbar Sort button that opens the sort sheet */
  SORT_BUTTON: "SCOUTING_SORT.SORT_BUTTON",
  /** Prefix for each sort radio option; suffix is the sort field key */
  OPTION_PREFIX: "SCOUTING_SORT.OPTION-",
  /** "Apply sort" primary button */
  APPLY_BUTTON: "SCOUTING_SORT.APPLY_BUTTON",
};

/** Mobile scouting All-Targets filter sheet controls (SE-4146). */
export const TEST_ID_SCOUTING_FILTER = {
  /** Toolbar Filters button that opens the filter sheet */
  FILTER_BUTTON: "SCOUTING_FILTER.FILTER_BUTTON",
  /** Footer "Discard" button (reverts draft edits) */
  DISCARD_BUTTON: "SCOUTING_FILTER.DISCARD_BUTTON",
  /** Footer "Show Results (N)" primary button (commits draft edits) */
  SHOW_RESULTS_BUTTON: "SCOUTING_FILTER.SHOW_RESULTS_BUTTON",
};

/**
 * The shared header back control (`StandardHeaderBackButton`), used by every
 * settings route. It renders a custom `backImage`, which replaces React
 * Navigation's default accessible name — so before SE-6812 it had none, and
 * the native flows tapped it by screen fraction (`point: '10%,10%'`). That is
 * fragile across device sizes and safe areas, and the missing name was a real
 * VoiceOver defect on every screen using it.
 */
export const TEST_ID_HEADER_BACK_BUTTON = "HEADER.BACK_BUTTON";

export const TEST_ID_NOTIFICATION_SETTINGS = {
  HEADING: "NOTIFICATION_SETTINGS.HEADING",
  ALLOW_ALL_TOGGLE: "NOTIFICATION_SETTINGS.ALLOW_ALL_TOGGLE",
};

export const TEST_ID_WHATS_NEW = {
  FILTER_BUTTON: "WHATS_NEW.FILTER_BUTTON",
};

export const TEST_ID_PLAYER_SEARCH = {
  INPUT: "PLAYER_SEARCH.INPUT",
  RESULT_ITEM: "PLAYER_SEARCH.RESULT_ITEM",
};

export const TEST_ID_PLAYER_COMPARISON = {
  YOUR_PLAYER_DROPDOWN: "PLAYER_COMPARISON.YOUR_PLAYER_DROPDOWN",
  LEAGUE_PLAYER_DROPDOWN: "PLAYER_COMPARISON.LEAGUE_PLAYER_DROPDOWN",
  PLAYER_OPTION: "PLAYER_COMPARISON.PLAYER_OPTION",
};

export const TEST_ID_CUSTOM_PROMPT = {
  INPUT: "CUSTOM_PROMPT.INPUT",
  SAVE_BUTTON: "CUSTOM_PROMPT.SAVE_BUTTON",
  CANCEL_BUTTON: "CUSTOM_PROMPT.CANCEL_BUTTON",
};

export const TEST_ID_CREATE_WATCHLIST_MODAL = {
  ROOT: "CREATE_WATCHLIST_MODAL.ROOT",
  INPUT: "CREATE_WATCHLIST_MODAL.INPUT",
  CREATE_BUTTON: "CREATE_WATCHLIST_MODAL.CREATE_BUTTON",
  CANCEL_BUTTON: "CREATE_WATCHLIST_MODAL.CANCEL_BUTTON",
};

export const TEST_ID_SEARCH = {
  RECOMMENDATION_ITEM: "SEARCH.RECOMMENDATION_ITEM",
};

export const TEST_ID_ADD_TO_LIST = {
  FAB: "ADD_TO_LIST.FAB",
  SHEET: "ADD_TO_LIST.SHEET",
  SEARCH: "ADD_TO_LIST.SEARCH",
  LIST_PICKER_SHEET: "ADD_TO_LIST.LIST_PICKER_SHEET",
};

export const TEST_ID_PLAYER_KANBAN = {
  // Board Management
  BOARD_TAB: "PLAYER_KANBAN.BOARD_TAB",
  CREATE_BOARD_BUTTON: "PLAYER_KANBAN.CREATE_BOARD_BUTTON",
  BOARD_OPTIONS_MENU: "PLAYER_KANBAN.BOARD_OPTIONS_MENU",
  BOARD_DROPDOWN_TOGGLE: "PLAYER_KANBAN.BOARD_DROPDOWN_TOGGLE",
  BOARD_DROPDOWN_ITEM: "PLAYER_KANBAN.BOARD_DROPDOWN_ITEM",
  BOARD_COUNT_INDICATOR: "PLAYER_KANBAN.BOARD_COUNT_INDICATOR",
  DELETE_BOARD_BUTTON: "PLAYER_KANBAN.DELETE_BOARD_BUTTON",
  RENAME_BOARD_INPUT: "PLAYER_KANBAN.RENAME_BOARD_INPUT",
  RENAME_BOARD_SAVE_BUTTON: "PLAYER_KANBAN.RENAME_BOARD_SAVE_BUTTON",
  RENAME_BOARD_CANCEL_BUTTON: "PLAYER_KANBAN.RENAME_BOARD_CANCEL_BUTTON",
  PIPELINE_NAME: "PLAYER_KANBAN.PIPELINE_NAME",

  // Columns
  KANBAN_COLUMN: "PLAYER_KANBAN.KANBAN_COLUMN",
  COLUMN_HEADER: "PLAYER_KANBAN.COLUMN_HEADER",
  // SE-6489: the designed "..." stage overflow menu. Replaces the inline
  // pencil/trash icons AND the standalone sort trigger (COLUMN_SORT_MENU),
  // which now live as rows inside this menu.
  COLUMN_OPTIONS_MENU: "PLAYER_KANBAN.COLUMN_OPTIONS_MENU",
  COLUMN_SORT_MENU: "PLAYER_KANBAN.COLUMN_SORT_MENU",
  COLUMN_COUNT_BADGE: "PLAYER_KANBAN.COLUMN_COUNT_BADGE",
  EMPTY_COLUMN_PLACEHOLDER: "PLAYER_KANBAN.EMPTY_COLUMN_PLACEHOLDER",
  ADD_COLUMN_BUTTON: "PLAYER_KANBAN.ADD_COLUMN_BUTTON",
  EDIT_COLUMN_BUTTON: "PLAYER_KANBAN.EDIT_COLUMN_BUTTON",
  DELETE_COLUMN_BUTTON: "PLAYER_KANBAN.DELETE_COLUMN_BUTTON",
  SAVE_COLUMN_BUTTON: "PLAYER_KANBAN.SAVE_COLUMN_BUTTON",
  CANCEL_COLUMN_BUTTON: "PLAYER_KANBAN.CANCEL_COLUMN_BUTTON",

  // SE-5491: Kanban ⇄ Table view toggle (board chrome, in the TopPanel action bar)
  VIEW_TOGGLE: "PLAYER_KANBAN.VIEW_TOGGLE",
  VIEW_TOGGLE_KANBAN: "PLAYER_KANBAN.VIEW_TOGGLE_KANBAN",
  VIEW_TOGGLE_TABLE: "PLAYER_KANBAN.VIEW_TOGGLE_TABLE",

  // SE-5491: Table view. TABLE_ROW / COLUMN_CARD encode `<stageId>.<playerId>`
  // so a dual-view consistency check can compare per-stage card-ID SETS across
  // the two renderers without relying on visible text.
  TABLE_VIEW: "PLAYER_KANBAN.TABLE_VIEW",
  TABLE_EMPTY: "PLAYER_KANBAN.TABLE_EMPTY",
  TABLE_ROW_PREFIX: "PLAYER_KANBAN.TABLE_ROW.",
  tableRowFor: (stageId: string, playerId: string): string =>
    `PLAYER_KANBAN.TABLE_ROW.${stageId}.${playerId}`,
  COLUMN_CARD_PREFIX: "PLAYER_KANBAN.COLUMN_CARD.",
  columnCardFor: (columnId: string, playerId: string): string =>
    `PLAYER_KANBAN.COLUMN_CARD.${columnId}.${playerId}`,
  tableHeaderFor: (columnKey: string): string =>
    `PLAYER_KANBAN.TABLE_HEADER.${columnKey}`,

  // SE-5495: sensitive-stage reason prompt. Shown before a card enters a stage
  // whose rule requires a reason; the submitted text becomes the MOVED entry's
  // `rationale` on the pipeline activity log.
  STAGE_MOVE_PROMPT: "PLAYER_KANBAN.STAGE_MOVE_PROMPT",
  STAGE_MOVE_PROMPT_INPUT: "PLAYER_KANBAN.STAGE_MOVE_PROMPT_INPUT",
  STAGE_MOVE_PROMPT_SUBMIT: "PLAYER_KANBAN.STAGE_MOVE_PROMPT_SUBMIT",
  STAGE_MOVE_PROMPT_CANCEL: "PLAYER_KANBAN.STAGE_MOVE_PROMPT_CANCEL",

  // SE-5496: terminal-stage outcome selector inside the reason prompt. Options
  // are rendered from the board template's ordered validOutcomes.
  STAGE_MOVE_PROMPT_OUTCOMES: "PLAYER_KANBAN.STAGE_MOVE_PROMPT_OUTCOMES",
  stageMoveOutcomeOptionFor: (outcome: string): string =>
    `PLAYER_KANBAN.STAGE_MOVE_PROMPT_OUTCOME.${outcome}`,

  // SE-5496: collapsed Closed area at the end of the board, grouped by outcome.
  CLOSED_AREA: "PLAYER_KANBAN.CLOSED_AREA",
  CLOSED_AREA_TOGGLE: "PLAYER_KANBAN.CLOSED_AREA_TOGGLE",
  closedGroupFor: (outcome: string): string =>
    `PLAYER_KANBAN.CLOSED_GROUP.${outcome}`,
  // SE-6862: each outcome group shows one card; the rest sit behind an
  // expand-in-place "+N more" control, and every card carries an overflow menu.
  CLOSED_GROUP_MORE: "PLAYER_KANBAN.CLOSED_GROUP_MORE",
  CLOSED_CARD_MENU: "PLAYER_KANBAN.CLOSED_CARD_MENU",
  // SE-5496: reopen a closed card into a working stage.
  REOPEN_CARD_BUTTON: "PLAYER_KANBAN.REOPEN_CARD_BUTTON",
  REOPEN_DIALOG: "PLAYER_KANBAN.REOPEN_DIALOG",
  REOPEN_DIALOG_SUBMIT: "PLAYER_KANBAN.REOPEN_DIALOG_SUBMIT",
  REOPEN_DIALOG_CANCEL: "PLAYER_KANBAN.REOPEN_DIALOG_CANCEL",
  reopenStageOptionFor: (phaseId: string): string =>
    `PLAYER_KANBAN.REOPEN_STAGE.${phaseId}`,

  // SE-5488: stage editor (rename, reorder, add, remove + protected/terminal
  // flag editing). Opened from the pipeline toolbar; scoped to one board.
  STAGE_EDITOR_OPEN: "PLAYER_KANBAN.STAGE_EDITOR_OPEN",
  STAGE_EDITOR: "PLAYER_KANBAN.STAGE_EDITOR",
  STAGE_EDITOR_CLOSE: "PLAYER_KANBAN.STAGE_EDITOR_CLOSE",
  // The collapsed dashed "+ Add stage" row. Pressing it expands the input +
  // confirm below; the input and submit ids only exist once expanded.
  STAGE_EDITOR_ADD_OPEN: "PLAYER_KANBAN.STAGE_EDITOR_ADD_OPEN",
  STAGE_EDITOR_ADD_INPUT: "PLAYER_KANBAN.STAGE_EDITOR_ADD_INPUT",
  STAGE_EDITOR_ADD_SUBMIT: "PLAYER_KANBAN.STAGE_EDITOR_ADD_SUBMIT",
  STAGE_EDITOR_DELETE_DIALOG: "PLAYER_KANBAN.STAGE_EDITOR_DELETE_DIALOG",
  // SE-6554: the modal's footer pair. Both dismiss — see the note in
  // StageEditorView on why there is nothing to stage or discard today.
  STAGE_EDITOR_CANCEL: "PLAYER_KANBAN.STAGE_EDITOR_CANCEL",
  STAGE_EDITOR_SAVE: "PLAYER_KANBAN.STAGE_EDITOR_SAVE",
  stageEditorRowFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_ROW.${phaseId}`,
  stageEditorRenameInputFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_RENAME_INPUT.${phaseId}`,
  stageEditorRenameSaveFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_RENAME_SAVE.${phaseId}`,
  stageEditorRenameStartFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_RENAME_START.${phaseId}`,
  stageEditorProtectedToggleFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_PROTECTED.${phaseId}`,
  stageEditorTerminalToggleFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_TERMINAL.${phaseId}`,
  // SE-5497: per-stage time-in-stage target (days) input in the stage editor.
  stageEditorTimeTargetFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_TIME_TARGET.${phaseId}`,
  // The menu row that reveals the target input. The input itself keeps
  // `stageEditorTimeTargetFor` and only mounts once this has been pressed.
  stageEditorTimeTargetOpenFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_TIME_TARGET_OPEN.${phaseId}`,
  // The quiet "target 14d" text on the row. Renders only when a target is SET,
  // so its absence means "no target", never "the row failed to draw it".
  stageEditorTimeTargetValueFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_TIME_TARGET_VALUE.${phaseId}`,
  stageEditorMoveUpFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_MOVE_UP.${phaseId}`,
  stageEditorMoveDownFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_MOVE_DOWN.${phaseId}`,
  stageEditorDeleteFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_DELETE.${phaseId}`,
  // SE-6554: the row's overflow menu (rename + flags + delete moved behind it)
  // and the inline protection notice under a flagged stage.
  stageEditorMenuFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_MENU.${phaseId}`,
  stageEditorProtectionNoticeFor: (phaseId: string): string =>
    `PLAYER_KANBAN.STAGE_EDITOR_PROTECTION_NOTICE.${phaseId}`,

  // Player Cards
  PLAYER_CARD: "PLAYER_KANBAN.PLAYER_CARD",
  PLAYER_CARD_DRAG_HANDLE: "PLAYER_KANBAN.PLAYER_CARD_DRAG_HANDLE",
  // SE-5497: over-target stale flag. Pipeline-only.
  //
  // SE-6490 removed the card-face rank prefix and the day COUNT when the card
  // was rebuilt against Figma `Player Card v7`; both are reachable from the
  // preview drawer (SE-5494), and days-in-stage still drives the stale flag.
  // Their ids are retired rather than left declared-but-unrendered: a dead
  // test id passes `toHaveCount(0)` for the wrong reason forever (SE-6262).
  PLAYER_CARD_STALE_FLAG: "PLAYER_KANBAN.PLAYER_CARD_STALE_FLAG",
  // SE-4685: grab handle for within-column rank drag-reorder; renders only
  // when a column is sorted by Rank.
  PLAYER_CARD_RANK_DRAG_HANDLE: "PLAYER_KANBAN.PLAYER_CARD_RANK_DRAG_HANDLE",
  // SE-5492: card avatar. Always the INITIALS fallback today — Player.imageUrl
  // (SE-5538) is schema-only and null for every player, so no photo renders.
  PLAYER_CARD_AVATAR: "PLAYER_KANBAN.PLAYER_CARD_AVATAR",
  // SE-5492: explicit position (distinct from the role archetype). SE-6490
  // renders it as the design's position CHIP rather than inline text.
  PLAYER_CARD_POSITION: "PLAYER_KANBAN.PLAYER_CARD_POSITION",
  // SE-5492: scout-reports count + verdict (latest report's overall score).
  // SE-6490 renders both as PILLS in the signals row instead of `Label: value`
  // text; the verdict pill reads `Verdict <score>`.
  PLAYER_CARD_REPORTS: "PLAYER_KANBAN.PLAYER_CARD_REPORTS",
  PLAYER_CARD_VERDICT: "PLAYER_KANBAN.PLAYER_CARD_VERDICT",
  // SE-6490 (Figma `Player Card v7`): GPR score badge, the `club · age ·
  // contract` context line, the signals row, the removable tag chips with
  // their `+N` overflow, and the muted "Updated …" footer.
  PLAYER_CARD_GPR: "PLAYER_KANBAN.PLAYER_CARD_GPR",
  PLAYER_CARD_META: "PLAYER_KANBAN.PLAYER_CARD_META",
  PLAYER_CARD_SIGNALS: "PLAYER_KANBAN.PLAYER_CARD_SIGNALS",
  PLAYER_CARD_TAGS: "PLAYER_KANBAN.PLAYER_CARD_TAGS",
  PLAYER_CARD_TAG_OVERFLOW: "PLAYER_KANBAN.PLAYER_CARD_TAG_OVERFLOW",
  PLAYER_CARD_UPDATED: "PLAYER_KANBAN.PLAYER_CARD_UPDATED",
  // SE-5492: sentinel that drives a column's lazy card mounting. Present only
  // while a column still has unmounted cards below the fold.
  COLUMN_LAZY_SENTINEL: "PLAYER_KANBAN.COLUMN_LAZY_SENTINEL",
  // SE-6554: the space a lane opens for the card being dragged into it. Present
  // for the duration of a drag once that lane has been entered; its HEIGHT (not
  // its presence) is what says whether the gap is currently open.
  COLUMN_DROP_GAP: "PLAYER_KANBAN.COLUMN_DROP_GAP",
  // SE-6554 (Figma 80:16392): the dashed CTA outline a lane shows while the
  // dragged card is aimed at it. Present ONLY on the lane that would receive
  // the card, so its presence is the assertion of which lane is the target.
  COLUMN_DRAG_OVER_RING: "PLAYER_KANBAN.COLUMN_DRAG_OVER_RING",

  // Tags
  //
  // TAG_MODAL and PLAYER_TAG_BADGE were removed: both were declared here (and
  // mirrored in `selectors.ts`) but rendered NOWHERE in `src/`. A dead id is
  // worse than a missing one, because `toHaveCount(0)` against it passes
  // forever for the wrong reason — the fake-pass class this suite has already
  // been bitten by. The tag editor is real; it opens as a `role="dialog"` with
  // a null `data-testid`, so address it by role rather than re-adding an id
  // nothing sets.
  TAG_MANAGER_BUTTON: "PLAYER_KANBAN.TAG_MANAGER_BUTTON",
  CREATE_TAG_INPUT: "PLAYER_KANBAN.CREATE_TAG_INPUT",
  TAG_COLOR_PICKER: "PLAYER_KANBAN.TAG_COLOR_PICKER",
  TAG_ITEM: "PLAYER_KANBAN.TAG_ITEM",
  DELETE_TAG_BUTTON: "PLAYER_KANBAN.DELETE_TAG_BUTTON",

  // Filters
  // FILTER_DROPDOWN removed: SE-6488 split the board header into a title row
  // and a toolbar row, and the filter control moved to
  // TEST_ID_BOARD_TOOLBAR.FILTERS_BUTTON. The old id stayed declared here and
  // rendered nowhere, so three big-boards assertions waited 30s on an element
  // that could never appear. Same failure class as the tagModal note above.
  SEARCH_INPUT: "PLAYER_KANBAN.SEARCH_INPUT",
  // COUNTRY_FILTER removed (SE-7450): the same failure class as FILTER_DROPDOWN
  // above, one step worse. It was rendered by NO component in `src/` and never
  // had been, so big-boards' `if (count() > 0)` branch was dead from the day it
  // was written — the country half of SE-4495 read as covered while asserting
  // nothing. The parity panel has a real Nationalities control now, and
  // big-boards asserts it unconditionally.
  //
  // AGE_SLIDER and MARKET_VALUE_SLIDER are KEPT deliberately: SE-7405 pointed
  // the board's Filters button at the shared panel, but `FilterModalView` still
  // sets both ids, so they are unreachable at runtime rather than undeclared.
  // Retiring them belongs with retiring that component, not here.
  AGE_SLIDER: "PLAYER_KANBAN.AGE_SLIDER",
  MARKET_VALUE_SLIDER: "PLAYER_KANBAN.MARKET_VALUE_SLIDER",

  // Sidebar
  PLAYER_SIDEBAR: "PLAYER_KANBAN.PLAYER_SIDEBAR",
  // Right-side details panel opened by clicking a board card (distinct from
  // PLAYER_SIDEBAR, the always-visible left drag-source sidebar).
  PLAYER_DETAILS_SIDEBAR: "PLAYER_KANBAN.PLAYER_DETAILS_SIDEBAR",
  SIDEBAR_CLOSE_BUTTON: "PLAYER_KANBAN.SIDEBAR_CLOSE_BUTTON",
  SIDEBAR_PLAYER_CARD: "PLAYER_KANBAN.SIDEBAR_PLAYER_CARD",
  SIDEBAR_STATS: "PLAYER_KANBAN.SIDEBAR_STATS",
};

// SE-5490: the add-players panel — the pipeline surface's replacement for the
// legacy two-way watchlist/roster source selector. Contextual source tabs
// (which tabs render depends on the pipeline's motion), a persistent global
// search field that bypasses tab scoping, and the Suggested tab's per-candidate
// reason chips, Add and Dismiss.
export const TEST_ID_ADD_PLAYERS_PANEL = {
  /**
   * The drawer surface itself. Present ONLY while the drawer is open — SE-6554
   * re-housed the panel from an inline rail into the shared overlay `Drawer`,
   * which mounts no subtree when closed. There is deliberately no
   * `EXPAND_TOGGLE` counterpart: the rail's collapsed state no longer exists,
   * and the affordance that opens the drawer is the board toolbar's
   * `BOARD_TOOLBAR.ADD_PLAYER_BUTTON`.
   */
  ROOT: "ADD_PLAYERS_PANEL.ROOT",
  COLLAPSE_TOGGLE: "ADD_PLAYERS_PANEL.COLLAPSE_TOGGLE",
  /** SE-6947 (PC1): the footer's Done button — closes the drawer. */
  DONE_BUTTON: "ADD_PLAYERS_PANEL.DONE_BUTTON",
  /**
   * One per rendered source tab; suffixed with the source key.
   *
   * SE-6265: these announce selection through `role="tab"` +
   * `aria-selected` — the pairing the app's own `TAB_BAR.*` items use, and
   * the one react-native-web actually maps to the DOM. A spec reads
   * `toHaveAttribute("aria-selected", …)`, expecting `"false"` on the
   * unselected tabs as well as `"true"` on the selected one. (Contrast the
   * multi-select `PIPELINE_ACTIVITY_LOG` chips further down, which are
   * `role="checkbox"` / `aria-checked` because `aria-selected` is not valid
   * on a button.)
   */
  SOURCE_TAB: (source: string) => `ADD_PLAYERS_PANEL.SOURCE_TAB.${source}`,
  SEARCH_INPUT: "ADD_PLAYERS_PANEL.SEARCH_INPUT",
  /** SE-6948 (PC2): the × inside the search field. Clearing restores the tabs. */
  SEARCH_CLEAR: "ADD_PLAYERS_PANEL.SEARCH_CLEAR",
  // SE-6949 (PC3): opens the in-drawer FILTERS PANEL, which replaces the list
  // in place. This superseded the SE-6554 filter-by-watchlist SelectDropdown —
  // the drawer now offers the full Scouting filter vocabulary (Reason · Basic ·
  // Financial · Fit & Ratings · Performance · Goalkeeper).
  FILTERS_BUTTON: "ADD_PLAYERS_PANEL.FILTERS_BUTTON",
  /** SE-6949 (PC3): the in-drawer filters panel root. */
  FILTER_PANEL: "ADD_PLAYERS_PANEL.FILTER_PANEL",
  /** SE-6949: the panel header's CLEAR ALL — resets every draft group. */
  FILTER_PANEL_CLEAR_ALL: "ADD_PLAYERS_PANEL.FILTER_PANEL_CLEAR_ALL",
  /** SE-6949: discard pending selections and return to the list. */
  FILTER_PANEL_CANCEL: "ADD_PLAYERS_PANEL.FILTER_PANEL_CANCEL",
  /** SE-6949: commit the draft and show the filtered results (PC7). */
  FILTER_PANEL_SHOW_RESULTS: "ADD_PLAYERS_PANEL.FILTER_PANEL_SHOW_RESULTS",
  /**
   * SE-7450: one range row inside the panel, keyed by its field
   * (`age`, `months`, `cost`, and the metric keys).
   *
   * `RangeFilterField` hardcodes `range-filter-min-input` /
   * `range-filter-max-input` on its two inputs, and those repeat once per row
   * — BASIC alone renders two rows, PERFORMANCE four — so before this id there
   * was no way to name a particular field. That is what left SE-4495's
   * age/market-value assertions with nothing to bind to after SE-7405 swapped
   * the board's panel.
   */
  FILTER_RANGE: (key: string) => `ADD_PLAYERS_PANEL.FILTER_RANGE.${key}`,
  /**
   * SE-7450: one collapsible section header inside the panel.
   *
   * Every section here mounts COLLAPSED (`useFilterPanelLogic` defaults them
   * all to `false`), unlike the FilterModal this panel replaced, where BASIC
   * and FINANCIAL were open on mount. A spec that wants a control inside a
   * section must therefore expand it first, and this is the handle it presses.
   */
  FILTER_SECTION: (key: string) => `ADD_PLAYERS_PANEL.FILTER_SECTION.${key}`,
  /** SE-6953 (PC7): the filtered-results body (tabs hidden). */
  FILTERED_RESULTS: "ADD_PLAYERS_PANEL.FILTERED_RESULTS",
  /** SE-6953: the "N RESULTS · N FILTERS APPLIED" summary row. */
  FILTERED_SUMMARY: "ADD_PLAYERS_PANEL.FILTERED_SUMMARY",
  /** SE-6953: the summary row's CLEAR ALL — removes every applied filter. */
  FILTERED_CLEAR_ALL: "ADD_PLAYERS_PANEL.FILTERED_CLEAR_ALL",
  /** SE-6953: a removable applied-filter chip; suffixed with the chip key. */
  FILTERED_CHIP: (key: string) => `ADD_PLAYERS_PANEL.FILTERED_CHIP.${key}`,
  // SE-6554 (Figma 80:15683): the GPR sort. A SelectDropdown TRIGGER, not a
  // button — pressing it opens the Default / Highest first / Lowest first menu,
  // and the selection sorts whichever tab is visible.
  SORT_BUTTON: "ADD_PLAYERS_PANEL.SORT_BUTTON",
  SEARCH_RESULTS: "ADD_PLAYERS_PANEL.SEARCH_RESULTS",
  /**
   * SE-6948 (PC2): a search-result group header; suffixed with the group key
   * (`in-pipeline` | `recently-viewed` | `all-players`).
   */
  SEARCH_GROUP: (key: string) => `ADD_PLAYERS_PANEL.SEARCH_GROUP.${key}`,
  /**
   * SE-6948/SE-6951: the muted "In list" marker on a row whose player already
   * sits in this pipeline; suffixed with the player id.
   */
  ROW_IN_LIST: (playerId: string) =>
    `ADD_PLAYERS_PANEL.ROW_IN_LIST.${playerId}`,
  SUGGESTED_LIST: "ADD_PLAYERS_PANEL.SUGGESTED_LIST",
  SUGGESTED_EMPTY: "ADD_PLAYERS_PANEL.SUGGESTED_EMPTY",
  /** A candidate/result row; suffixed with the player id. */
  ROW: (playerId: string) => `ADD_PLAYERS_PANEL.ROW.${playerId}`,
  /**
   * SE-7092 (PC1): the row's leading six-dot drag glyph. Named separately
   * from `ROW` so a spec/test can assert the glyph specifically rather than
   * only the row container — the QA finding this closes was exactly that the
   * glyph was visually indistinguishable from absent with nothing to bind to.
   */
  ROW_DRAG_HANDLE: (playerId: string) =>
    `ADD_PLAYERS_PANEL.ROW_DRAG_HANDLE.${playerId}`,
  ROW_ADD: (playerId: string) => `ADD_PLAYERS_PANEL.ROW_ADD.${playerId}`,
  ROW_DISMISS: (playerId: string) =>
    `ADD_PLAYERS_PANEL.ROW_DISMISS.${playerId}`,
  /**
   * A reason chip; suffixed with the SuggestionReasonCategory value.
   *
   * SE-6265: hovering reveals its explanation transiently, CLICKING pins it
   * (so it survives the pointer leaving), and clicking the pinned chip
   * dismisses it. The full explanation is also the chip's `aria-label`, which
   * is what a spec compares the revealed text against.
   */
  REASON_CHIP: (category: string) =>
    `ADD_PLAYERS_PANEL.REASON_CHIP.${category}`,
  /** The expanded full explanation for the chips on a row. At most one. */
  REASON_EXPLANATION: "ADD_PLAYERS_PANEL.REASON_EXPLANATION",
};

// SE-5494: player preview drawer — opened by clicking a board card, replacing
// the old 4-row PlayerDetailsSidebar. Sections, top to bottom: identity + GPR,
// the pinned latest note, the stage selector, key info, ratings & style fit,
// reports & verdict, season form, tags, the (SE-5504-reserved) Activity slot,
// and the footer actions.
//
// The drawer ALSO carries the legacy PLAYER_KANBAN.PLAYER_DETAILS_SIDEBAR and
// SIDEBAR_CLOSE_BUTTON ids on its root and close control, so the specs written
// against the panel it replaces (kanban-pipeline.spec.ts, big-boards.spec.ts)
// keep passing against the new surface rather than being rewritten.
export const TEST_ID_PLAYER_PREVIEW_DRAWER = {
  ROOT: "PLAYER_PREVIEW_DRAWER.ROOT",
  CLOSE: "PLAYER_PREVIEW_DRAWER.CLOSE",
  LOADING: "PLAYER_PREVIEW_DRAWER.LOADING",
  ERROR_STATE: "PLAYER_PREVIEW_DRAWER.ERROR_STATE",

  // Identity + GPR
  PLAYER_NAME: "PLAYER_PREVIEW_DRAWER.PLAYER_NAME",
  PLAYER_CLUB: "PLAYER_PREVIEW_DRAWER.PLAYER_CLUB",
  GPR_BADGE: "PLAYER_PREVIEW_DRAWER.GPR_BADGE",
  // The avatar's availability dot. Absent whenever the provider reported no
  // `isInjured` value — its absence means "unknown", never "available".
  AVAILABILITY_DOT: "PLAYER_PREVIEW_DRAWER.AVAILABILITY_DOT",

  // AC1/AC2: the single most recent note, pinned ABOVE the stage section.
  // Neither id renders when the player has no notes.
  PINNED_NOTE: "PLAYER_PREVIEW_DRAWER.PINNED_NOTE",
  PINNED_NOTE_BODY: "PLAYER_PREVIEW_DRAWER.PINNED_NOTE_BODY",
  // Time-boxed recency badge. The note pins at ANY age; only this badge is
  // bounded by the recency window, so its absence is not the note's absence.
  PINNED_NOTE_NEW_BADGE: "PLAYER_PREVIEW_DRAWER.PINNED_NOTE_NEW_BADGE",
  VIEW_ALL_NOTES: "PLAYER_PREVIEW_DRAWER.VIEW_ALL_NOTES",

  // AC4: stage selector. Changing it routes through the SAME move service as
  // drag, so a protected/terminal target raises the shared stage-move prompt.
  STAGE_SECTION: "PLAYER_PREVIEW_DRAWER.STAGE_SECTION",
  CURRENT_STAGE: "PLAYER_PREVIEW_DRAWER.CURRENT_STAGE",
  stageOptionFor: (phaseId: string): string =>
    `PLAYER_PREVIEW_DRAWER.STAGE_OPTION.${phaseId}`,

  // Sections
  KEY_INFO_SECTION: "PLAYER_PREVIEW_DRAWER.KEY_INFO_SECTION",
  RATINGS_SECTION: "PLAYER_PREVIEW_DRAWER.RATINGS_SECTION",
  ATTRIBUTES_SECTION: "PLAYER_PREVIEW_DRAWER.ATTRIBUTES_SECTION",
  REPORTS_SECTION: "PLAYER_PREVIEW_DRAWER.REPORTS_SECTION",
  REPORTS_EMPTY: "PLAYER_PREVIEW_DRAWER.REPORTS_EMPTY",
  VERDICT: "PLAYER_PREVIEW_DRAWER.VERDICT",
  reportRowFor: (reportKey: string): string =>
    `PLAYER_PREVIEW_DRAWER.REPORT_ROW.${reportKey}`,
  SEASON_FORM_SECTION: "PLAYER_PREVIEW_DRAWER.SEASON_FORM_SECTION",
  TAGS_SECTION: "PLAYER_PREVIEW_DRAWER.TAGS_SECTION",
  TAGS_EMPTY: "PLAYER_PREVIEW_DRAWER.TAGS_EMPTY",
  // SE-6859: the tags section's "+" add affordance (edit access only).
  TAGS_ADD: "PLAYER_PREVIEW_DRAWER.TAGS_ADD",
  tagFor: (tagId: string): string => `PLAYER_PREVIEW_DRAWER.TAG.${tagId}`,

  // SE-5504 filled the Activity slot SE-5494 reserved: the card-scoped audit
  // trail, newest-first, rendered exactly as the server returns it.
  ACTIVITY_SECTION: "PLAYER_PREVIEW_DRAWER.ACTIVITY_SECTION",
  ACTIVITY_TRAIL: "PLAYER_PREVIEW_DRAWER.ACTIVITY_TRAIL",

  // Footer. The remove button renders only for viewers with EDIT access
  // (SE-5500) and opens the removal confirmation below.
  OPEN_PROFILE: "PLAYER_PREVIEW_DRAWER.OPEN_PROFILE",
  REMOVE_FROM_PIPELINE: "PLAYER_PREVIEW_DRAWER.REMOVE_FROM_PIPELINE",

  // SE-5500 removal confirmation. The child ids are DERIVED by the shared
  // ConfirmDialog molecule from the root id, so they are spelled
  // out here rather than re-derived at each call site.
  REMOVE_DIALOG: "PLAYER_PREVIEW_DRAWER.REMOVE_DIALOG",
  REMOVE_DIALOG_CANCEL: "PLAYER_PREVIEW_DRAWER.REMOVE_DIALOG-cancel",
  REMOVE_DIALOG_CONFIRM: "PLAYER_PREVIEW_DRAWER.REMOVE_DIALOG-confirm",
  REMOVE_DIALOG_REASON: "PLAYER_PREVIEW_DRAWER.REMOVE_DIALOG-reason",
  REMOVE_DIALOG_ERROR: "PLAYER_PREVIEW_DRAWER.REMOVE_DIALOG-error",

  // One label/value row inside any section; `key` is the row's stable key
  // from playerPreviewModel (e.g. "agent", "gpr", "minutes").
  infoRowFor: (key: string): string => `PLAYER_PREVIEW_DRAWER.INFO_ROW.${key}`,
  infoValueFor: (key: string): string =>
    `PLAYER_PREVIEW_DRAWER.INFO_VALUE.${key}`,
};

// SE-5010 + SE-5129: List header (desktop). Rendered as the first child of
// the AllTargetsTable card (above the sticky thead) when a specific list pill
// is active (hidden on All Targets and My Roster). Owns identity (name +
// metadata), inline rename, role-aware Shared badge, and the single 3-dot
// kebab menu (Edit name + Share list collapsed into the menu). SE-5129
// removed the standalone Share/pencil icons; SE-5013 will replace the
// placeholder toasts wired to Share/Archive/Leave.
export const TEST_ID_WATCHLIST_HEADER = {
  ROOT: "WATCHLIST_HEADER.ROOT",
  NAME: "WATCHLIST_HEADER.NAME",
  NAME_INPUT: "WATCHLIST_HEADER.NAME_INPUT",
  // SE-8449: the shared name rule's inline message under the inline editor.
  NAME_ERROR: "WATCHLIST_HEADER.NAME_ERROR",
  METADATA: "WATCHLIST_HEADER.METADATA",
  SHARED_BADGE: "WATCHLIST_HEADER.SHARED_BADGE",
  SHARE_TRIGGER: "WATCHLIST_HEADER.SHARE_TRIGGER",
  MENU_TRIGGER: "WATCHLIST_HEADER.MENU_TRIGGER",
  menuItemFor: (id: string): string => `WATCHLIST_HEADER.MENU_ITEM.${id}`,
};

// SE-5013: Share watchlist modal (desktop). Search-filtered All Users list
// with avatar / name / role / checkbox rows, selection pills above the
// search, and a "Share with selection (N)" footer disabled until the
// selection diverges from the initially-shared set. Submit diffs against the
// initial shares and fires createUserWatchlist (added) + deleteUserWatchlist
// (removed) in parallel. This closes SE-4901 (broken sharing UI).
export const TEST_ID_WATCHLIST_SHARE_MODAL = {
  ROOT: "WATCHLIST_SHARE_MODAL.ROOT",
  CLOSE_BUTTON: "WATCHLIST_SHARE_MODAL.CLOSE_BUTTON",
};

// SE-8313 (PRD-6.1): the share model shared by the desktop modal and the
// mobile sheet — an invite row (search, `Can edit` / `Can view`, Invite) above
// the WHO HAS ACCESS list whose rows change role or revoke in place. One
// surface is open at a time, so both use the same ids.
export const TEST_ID_WATCHLIST_SHARE = {
  TOOLBAR_BUTTON: "WATCHLIST_SHARE.TOOLBAR_BUTTON",
  INVITE_SEARCH: "WATCHLIST_SHARE.INVITE_SEARCH",
  INVITE_ROLE: "WATCHLIST_SHARE.INVITE_ROLE",
  INVITE_BUTTON: "WATCHLIST_SHARE.INVITE_BUTTON",
  INVITE_SELECTED: "WATCHLIST_SHARE.INVITE_SELECTED",
  INVITE_SUGGESTIONS: "WATCHLIST_SHARE.INVITE_SUGGESTIONS",
  INVITE_ERROR: "WATCHLIST_SHARE.INVITE_ERROR",
  ACCESS_LIST: "WATCHLIST_SHARE.ACCESS_LIST",
  ACCESS_EMPTY: "WATCHLIST_SHARE.ACCESS_EMPTY",
  /** "Loading…" / "Couldn't load…" line shown until the grants are known. */
  ACCESS_STATE: "WATCHLIST_SHARE.ACCESS_STATE",
  DONE_BUTTON: "WATCHLIST_SHARE.DONE_BUTTON",
  candidateFor: (userId: string): string =>
    `WATCHLIST_SHARE.CANDIDATE.${userId}`,
  accessRowFor: (userId: string): string =>
    `WATCHLIST_SHARE.ACCESS_ROW.${userId}`,
  roleLabelFor: (userId: string): string =>
    `WATCHLIST_SHARE.ROLE_LABEL.${userId}`,
  roleSelectFor: (userId: string): string =>
    `WATCHLIST_SHARE.ROLE_SELECT.${userId}`,
  revokeFor: (userId: string): string => `WATCHLIST_SHARE.REVOKE.${userId}`,
  // SE-8359 (PV17): the "Everyone at {organization}" group row.
  GROUP_CANDIDATE: "WATCHLIST_SHARE.GROUP_CANDIDATE",
  GROUP_SELECTED: "WATCHLIST_SHARE.GROUP_SELECTED",
  groupRowFor: (groupGrantId: string): string =>
    `WATCHLIST_SHARE.GROUP_ROW.${groupGrantId}`,
  groupRoleSelectFor: (groupGrantId: string): string =>
    `WATCHLIST_SHARE.GROUP_ROLE_SELECT.${groupGrantId}`,
  groupRevokeFor: (groupGrantId: string): string =>
    `WATCHLIST_SHARE.GROUP_REVOKE.${groupGrantId}`,
  /** "Also editor via Everyone at …" under a person outranked by the group. */
  captionFor: (userId: string): string => `WATCHLIST_SHARE.CAPTION.${userId}`,
  /** "Inactive" pill on a person whose membership is not ACTIVE. */
  inactiveFor: (userId: string): string => `WATCHLIST_SHARE.INACTIVE.${userId}`,
};

// SE-5013: Archive (hard-delete) watchlist confirm modal (desktop, OWNER
// only). Destructive AlertDialog — red Delete confirm. On confirm fires
// deleteWatchlist, refetches the pills queries (the pill evicts on refetch),
// and switches the active pill to All Targets if the archived list was active.
export const TEST_ID_WATCHLIST_ARCHIVE_MODAL = {
  ROOT: "WATCHLIST_ARCHIVE_MODAL.ROOT",
  CANCEL_BUTTON: "WATCHLIST_ARCHIVE_MODAL.CANCEL_BUTTON",
  CONFIRM_BUTTON: "WATCHLIST_ARCHIVE_MODAL.CONFIRM_BUTTON",
};

// SE-5013: Leave shared watchlist confirm modal (desktop, EDITOR/VIEWER).
// Non-destructive AlertDialog — neutral Leave confirm. On confirm fires
// deleteUserWatchlist on the current user's membership row, refetches the
// pills queries (the pill drops from Shared-with-me on refetch), and switches
// the active pill to All Targets if the left list was active.
export const TEST_ID_WATCHLIST_LEAVE_MODAL = {
  ROOT: "WATCHLIST_LEAVE_MODAL.ROOT",
  CANCEL_BUTTON: "WATCHLIST_LEAVE_MODAL.CANCEL_BUTTON",
  CONFIRM_BUTTON: "WATCHLIST_LEAVE_MODAL.CONFIRM_BUTTON",
};

// SE-5011 + SE-5127: Interactive Watchlist column cell on the desktop
// All Targets table. Read state = chip-styled name(s) (SE-5127); click opens
// a popover anchored to the cell with checkbox rows for the scout's editable
// watchlists (OWNER/EDITOR only — closes SE-4923 at the UI by never offering
// a VIEWER-role list as a target). Sections: "My lists" (OWNER) / "Shared
// with me" (EDITOR). Toggles fire WatchlistAddPlayerToWatchlist /
// WatchlistRemovePlayerFromWatchlist with optimistic UX + refetch.
export const TEST_ID_WATCHLIST_COLUMN_CELL = {
  CELL: "WATCHLIST_COLUMN_CELL.CELL",
  TRIGGER: "WATCHLIST_COLUMN_CELL.TRIGGER",
  CHIP: "WATCHLIST_COLUMN_CELL.CHIP",
  EXTRA_COUNT: "WATCHLIST_COLUMN_CELL.EXTRA_COUNT",
  POPOVER_CONTENT: "WATCHLIST_COLUMN_CELL.POPOVER_CONTENT",
  EMPTY_STATE: "WATCHLIST_COLUMN_CELL.EMPTY_STATE",
  CREATE_NEW: "WATCHLIST_COLUMN_CELL.CREATE_NEW",
  sectionFor: (id: "my-lists" | "shared-with-me"): string =>
    `WATCHLIST_COLUMN_CELL.SECTION.${id}`,
  rowFor: (watchlistId: string): string =>
    `WATCHLIST_COLUMN_CELL.ROW.${watchlistId}`,
  checkboxFor: (watchlistId: string): string =>
    `WATCHLIST_COLUMN_CELL.CHECKBOX.${watchlistId}`,
};

// SE-5012: per-row actions (kebab) on the desktop Watchlists table. Trailing
// right-pinned column; the trigger is hover-revealed. Menu is pill-aware —
// "Remove from list" (only when a specific user list is active) + "Share
// player link" (always). Item ids: "remove", "share".
export const TEST_ID_ROW_ACTIONS = {
  CELL: "ROW_ACTIONS.CELL",
  TRIGGER: "ROW_ACTIONS.TRIGGER",
  menuItemFor: (id: string): string => `ROW_ACTIONS.MENU_ITEM.${id}`,
};

// SE-5026: Pills row (desktop). Replaces the legacy ListSelector dropdown on
// the Watchlists tab. Renders [All Targets (pinned)] + up to 3 recents
// (ordered by lastSelectedAt DESC, NULLS LAST) + a "+ See all lists" button
// that opens the SE-5004 drawer. Click on a non-active pill activates it and
// fires markUserWatchlistAsRecentlySelected so server-side recency bubbles
// the pill to position 2 on the next render.
export const TEST_ID_WATCHLIST_PILLS_ROW = {
  ROOT: "WATCHLIST_PILLS_ROW.ROOT",
  ALL_TARGETS_PILL: "WATCHLIST_PILLS_ROW.ALL_TARGETS_PILL",
  SEE_ALL_BTN: "WATCHLIST_PILLS_ROW.SEE_ALL_BTN",
  pillFor: (id: string): string => `WATCHLIST_PILLS_ROW.PILL.${id}`,
  selectionFor: (id: string): string => `WATCHLIST_PILLS_ROW.SELECTION.${id}`,
};

// SE-8316 (PRD-3.7 – 3.10): table row selection + the floating selection bar.
export const TEST_ID_WATCHLIST_SELECTION = {
  HEADER_CHECKBOX: "WATCHLIST_SELECTION.HEADER_CHECKBOX",
  BAR: "WATCHLIST_SELECTION.BAR",
  COPY: "WATCHLIST_SELECTION.COPY",
  MORE_TRIGGER: "WATCHLIST_SELECTION.MORE_TRIGGER",
  TOP_N_INPUT: "WATCHLIST_SELECTION.TOP_N_INPUT",
  COPY_BUTTON: "WATCHLIST_SELECTION.COPY_BUTTON",
  NEW_LIST_BUTTON: "WATCHLIST_SELECTION.NEW_LIST_BUTTON",
  PICKER: "WATCHLIST_SELECTION.PICKER",
  PICKER_FOOTER: "WATCHLIST_SELECTION.PICKER_FOOTER",
  PICKER_DONE: "WATCHLIST_SELECTION.PICKER_DONE",
  NEW_LIST_DIALOG: "WATCHLIST_SELECTION.NEW_LIST_DIALOG",
  NEW_LIST_NAME: "WATCHLIST_SELECTION.NEW_LIST_NAME",
  NEW_LIST_ERROR: "WATCHLIST_SELECTION.NEW_LIST_ERROR",
  NEW_LIST_SUBMIT: "WATCHLIST_SELECTION.NEW_LIST_SUBMIT",
  // SE-8335 (PV07): the inherited-formation line and the Unassigned preview
  // in the Name-it dialog.
  NEW_LIST_FORMATION: "WATCHLIST_SELECTION.NEW_LIST_FORMATION",
  NEW_LIST_UNASSIGNED: "WATCHLIST_SELECTION.NEW_LIST_UNASSIGNED",
  STALE_BANNER: "WATCHLIST_SELECTION.STALE_BANNER",
  STALE_RELOAD: "WATCHLIST_SELECTION.STALE_RELOAD",
  STALE_UNDO: "WATCHLIST_SELECTION.STALE_UNDO",
  COPY_TOAST_OPEN: "WATCHLIST_SELECTION.COPY_TOAST_OPEN",
  // SE-8334 (PV08): the picker's inline copy failure, and its Reload when the
  // destination was deleted or unshared under the open picker.
  COPY_ERROR: "WATCHLIST_SELECTION.COPY_ERROR",
  COPY_ERROR_RELOAD: "WATCHLIST_SELECTION.COPY_ERROR_RELOAD",
  // SE-8345 (PV15): the "Create scenario from selection" confirmation.
  SCENARIO_DIALOG: "WATCHLIST_SELECTION.SCENARIO_DIALOG",
  SCENARIO_NAME: "WATCHLIST_SELECTION.SCENARIO_NAME",
  SCENARIO_HELPER: "WATCHLIST_SELECTION.SCENARIO_HELPER",
  SCENARIO_ERROR: "WATCHLIST_SELECTION.SCENARIO_ERROR",
  SCENARIO_SUBMIT: "WATCHLIST_SELECTION.SCENARIO_SUBMIT",
  rowCheckboxFor: (playerId: string): string =>
    `WATCHLIST_SELECTION.ROW.${playerId}`,
  pickerRowFor: (watchlistId: string): string =>
    `WATCHLIST_SELECTION.PICKER_ROW.${watchlistId}`,
  pickerCheckboxFor: (watchlistId: string): string =>
    `WATCHLIST_SELECTION.PICKER_CHECKBOX.${watchlistId}`,
};

// SE-5489: pipeline chip switcher — the recruitment-pipeline sibling of the
// Scouting WATCHLIST_PILLS_ROW. Recent-pipeline chips (recency snapshot, cap 3)
// + an appended, highlighted active chip when the active pipeline is not one of
// the recents + a "See all pipelines ->" trigger that opens the drawer below.
// SE-6537 adds CREATE_BTN: the "+ New" pipeline-creation trigger. SE-5489
// replaced the legacy BoardTabs strip (which carried a "+ New" tab) with this
// chip row and dropped the creation entry point entirely, leaving no way to
// create a pipeline from the UI. CREATE_BTN renders in BOTH the populated and
// the empty chip states — the zero-pipeline case is exactly when a user most
// needs it.
export const TEST_ID_PIPELINE_CHIPS_ROW = {
  ROOT: "PIPELINE_CHIPS_ROW.ROOT",
  SEE_ALL_BTN: "PIPELINE_CHIPS_ROW.SEE_ALL_BTN",
  CREATE_BTN: "PIPELINE_CHIPS_ROW.CREATE_BTN",
  EMPTY_STATE: "PIPELINE_CHIPS_ROW.EMPTY_STATE",
  // SE-6597: the truncation affordance. Present only when the board-list walk
  // stopped while more pages existed, so its presence IS the assertion.
  TRUNCATION_INDICATOR: "PIPELINE_CHIPS_ROW.TRUNCATION_INDICATOR",
  chipFor: (id: string): string => `PIPELINE_CHIPS_ROW.CHIP.${id}`,
};

// SE-6488: the board toolbar row — row 2 of the Pipeline board header. Left:
// board-scoped player search, Filters, Sort. Right: the Review/Activity/Rules
// panel buttons, an overflow menu preserving Stages/Share/Tags (absent from the
// design frame, kept pending a product answer), and the `+ Add player` primary
// CTA. The title row above it carries PIPELINE_NAME / the category chip / the
// chip switcher / the Kanban|Table toggle.
export const TEST_ID_BOARD_TOOLBAR = {
  ROOT: "BOARD_TOOLBAR.ROOT",
  SEARCH_INPUT: "BOARD_TOOLBAR.SEARCH_INPUT",
  FILTERS_BUTTON: "BOARD_TOOLBAR.FILTERS_BUTTON",
  SORT_BUTTON: "BOARD_TOOLBAR.SORT_BUTTON",
  ADD_PLAYER_BUTTON: "BOARD_TOOLBAR.ADD_PLAYER_BUTTON",
  OVERFLOW_MENU: "BOARD_TOOLBAR.OVERFLOW_MENU",
  OVERFLOW_STAGES: "BOARD_TOOLBAR.OVERFLOW_STAGES",
  OVERFLOW_SHARE: "BOARD_TOOLBAR.OVERFLOW_SHARE",
  OVERFLOW_TAGS: "BOARD_TOOLBAR.OVERFLOW_TAGS",
  // SE-6863: shared default card order actions (pipeline boards only).
  OVERFLOW_SET_DEFAULT_ORDER: "BOARD_TOOLBAR.OVERFLOW_SET_DEFAULT_ORDER",
  OVERFLOW_RESET_ORDER: "BOARD_TOOLBAR.OVERFLOW_RESET_ORDER",
  sortOptionFor: (key: string): string => `BOARD_TOOLBAR.SORT_OPTION.${key}`,
  sortDirectionFor: (key: string): string =>
    `BOARD_TOOLBAR.SORT_DIRECTION.${key}`,
};

// SE-6488: the header title row's category tag chip (design node 80:14982) —
// the pipeline's recruitment category, rendered beside the pipeline name in
// place of the removed "Recruitment Pipeline Overview" subtitle.
export const TEST_ID_PIPELINE_CATEGORY_CHIP = {
  ROOT: "PIPELINE_CATEGORY_CHIP.ROOT",
};

// SE-5489: all-pipelines drawer — the recruitment-pipeline sibling of the
// Scouting SEE_ALL_LISTS_DRAWER. Grouped Personal / Team / System sections;
// each row shows type, owner/assignee, card count, and last activity.
export const TEST_ID_SEE_ALL_PIPELINES_DRAWER = {
  ROOT: "SEE_ALL_PIPELINES_DRAWER.ROOT",
  TITLE: "SEE_ALL_PIPELINES_DRAWER.TITLE",
  CLOSE: "SEE_ALL_PIPELINES_DRAWER.CLOSE",
  SEARCH_INPUT: "SEE_ALL_PIPELINES_DRAWER.SEARCH_INPUT",
  // SE-6554: creates a pipeline from inside the drawer. It CLOSES the drawer
  // and opens the New Pipeline modal — the modal is the only place a pipeline
  // is actually named and created, so this is a route to it, not a second
  // creation path.
  NEW_PIPELINE: "SEE_ALL_PIPELINES_DRAWER.NEW_PIPELINE",
  // SE-6554: row ordering within each section. A SelectDropdown TRIGGER, not a
  // button — "Recently used" (updatedAt DESC, the drawer's long-standing
  // default) or "Name (A–Z)".
  SORT_TRIGGER: "SEE_ALL_PIPELINES_DRAWER.SORT_TRIGGER",
  EMPTY_STATE: "SEE_ALL_PIPELINES_DRAWER.EMPTY_STATE",
  ERROR_STATE: "SEE_ALL_PIPELINES_DRAWER.ERROR_STATE",
  RETRY: "SEE_ALL_PIPELINES_DRAWER.RETRY",
  LOADING: "SEE_ALL_PIPELINES_DRAWER.LOADING",
  SECTION_PERSONAL: "SEE_ALL_PIPELINES_DRAWER.SECTION.PERSONAL",
  SECTION_TEAM: "SEE_ALL_PIPELINES_DRAWER.SECTION.TEAM",
  SECTION_SYSTEM: "SEE_ALL_PIPELINES_DRAWER.SECTION.SYSTEM",
  CHEVRON_PERSONAL: "SEE_ALL_PIPELINES_DRAWER.CHEVRON.PERSONAL",
  CHEVRON_TEAM: "SEE_ALL_PIPELINES_DRAWER.CHEVRON.TEAM",
  CHEVRON_SYSTEM: "SEE_ALL_PIPELINES_DRAWER.CHEVRON.SYSTEM",
  // SE-6775: the truncation affordance. Present only when the board-list walk
  // stopped while more pages existed, so its presence IS the assertion. Same
  // convention as PIPELINE_CHIPS_ROW.TRUNCATION_INDICATOR (SE-6597).
  TRUNCATION_INDICATOR: "SEE_ALL_PIPELINES_DRAWER.TRUNCATION_INDICATOR",
  rowFor: (id: string): string => `SEE_ALL_PIPELINES_DRAWER.ROW.${id}`,
  rowCountFor: (id: string): string =>
    `SEE_ALL_PIPELINES_DRAWER.ROW.COUNT.${id}`,
  rowMetaFor: (id: string): string => `SEE_ALL_PIPELINES_DRAWER.ROW.META.${id}`,
};

// SE-5015: Column config dropdown (desktop Scouting Watchlists table). Reworks
// the column picker so each column is a row with a checkbox (toggle = immediate
// show/hide) + drag handle (drag = immediate vertical reorder). The Player row is
// locked: disabled-checked checkbox, NO drag handle, always first. Visibility +
// order persist across reload via the existing @scouting_table_state AsyncStorage
// wiring. The trigger (Columns3 icon button) lives on the table toolbar.
export const TEST_ID_COLUMN_CONFIG = {
  TRIGGER: "COLUMN_CONFIG.TRIGGER",
  ROOT: "COLUMN_CONFIG.ROOT",
  PLAYER_ROW: "COLUMN_CONFIG.PLAYER_ROW",
  rowFor: (id: string): string => `COLUMN_CONFIG.ROW.${id}`,
  checkboxFor: (id: string): string => `COLUMN_CONFIG.CHECKBOX.${id}`,
  dragHandleFor: (id: string): string => `COLUMN_CONFIG.DRAG_HANDLE.${id}`,
};

// SE-5004: See all lists drawer (desktop). Referenced from the SeeAllListsDrawer
// component (Container + View + Row + Section) and from the Playwright e2e
// Validation Journey for SE-5004.
export const TEST_ID_SEE_ALL_LISTS_DRAWER = {
  TRIGGER: "SEE_ALL_LISTS_DRAWER.TRIGGER",
  ROOT: "SEE_ALL_LISTS_DRAWER.ROOT",
  CLOSE: "SEE_ALL_LISTS_DRAWER.CLOSE",
  TITLE: "SEE_ALL_LISTS_DRAWER.TITLE",
  // SE-5128: drawer-level search input.
  SEARCH_INPUT: "SEE_ALL_LISTS_DRAWER.SEARCH_INPUT",
  EMPTY_STATE: "SEE_ALL_LISTS_DRAWER.EMPTY_STATE",
  CREATE_NEW_LIST: "SEE_ALL_LISTS_DRAWER.CREATE_NEW_LIST",
  SECTION_MY: "SEE_ALL_LISTS_DRAWER.SECTION.MY",
  SECTION_SHARED: "SEE_ALL_LISTS_DRAWER.SECTION.SHARED",
  CHEVRON_MY: "SEE_ALL_LISTS_DRAWER.CHEVRON.MY",
  CHEVRON_SHARED: "SEE_ALL_LISTS_DRAWER.CHEVRON.SHARED",
  LOADING: "SEE_ALL_LISTS_DRAWER.LOADING",
  rowFor: (id: string): string => `SEE_ALL_LISTS_DRAWER.ROW.${id}`,
  rowCountFor: (id: string): string => `SEE_ALL_LISTS_DRAWER.ROW.COUNT.${id}`,
  rowSharedIconFor: (id: string): string =>
    `SEE_ALL_LISTS_DRAWER.ROW.SHARED_ICON.${id}`,
};

/**
 * Shared mobile bottom-sheet chrome anchors (SE-5081). Consumers compose
 *  their own content inside MobileSheetChrome; these 5 anchors locate the
 *  chrome itself (root container, close chip, drag handle, body scroll
 *  region, sticky footer).
 */
export const TEST_ID_MOBILE_SHEET_CHROME = {
  ROOT: "MOBILE_SHEET_CHROME.ROOT",
  CLOSE: "MOBILE_SHEET_CHROME.CLOSE",
  HANDLE: "MOBILE_SHEET_CHROME.HANDLE",
  BODY: "MOBILE_SHEET_CHROME.BODY",
  FOOTER: "MOBILE_SHEET_CHROME.FOOTER",
};

// SE-5074: Mobile "See all lists" bottom sheet — the mobile sibling of the
// desktop SeeAllListsDrawer (SE-5004). Rendered inside MobileSheetChrome (whose
// own chrome anchors live in TEST_ID_MOBILE_SHEET_CHROME — ROOT/CLOSE/HANDLE),
// so this set only locates the sheet-specific content: the "See all →" trigger
// pill on the Watchlists-tab pills row, the footer "+ Create new list" button,
// the two collapsible sections, and the per-row anchors.
export const TEST_ID_SEE_ALL_LISTS_SHEET = {
  TRIGGER: "SEE_ALL_LISTS_SHEET.TRIGGER",
  TITLE: "SEE_ALL_LISTS_SHEET.TITLE",
  SEARCH: "SEE_ALL_LISTS_SHEET.SEARCH",
  CREATE_NEW_LIST: "SEE_ALL_LISTS_SHEET.CREATE_NEW_LIST",
  SECTION_MY: "SEE_ALL_LISTS_SHEET.SECTION.MY",
  SECTION_SHARED: "SEE_ALL_LISTS_SHEET.SECTION.SHARED",
  CHEVRON_MY: "SEE_ALL_LISTS_SHEET.CHEVRON.MY",
  CHEVRON_SHARED: "SEE_ALL_LISTS_SHEET.CHEVRON.SHARED",
  LOADING: "SEE_ALL_LISTS_SHEET.LOADING",
  rowFor: (id: string): string => `SEE_ALL_LISTS_SHEET.ROW.${id}`,
  rowCountFor: (id: string): string => `SEE_ALL_LISTS_SHEET.ROW.COUNT.${id}`,
};

// SE-5077: Mobile Share full-screen sheet — the mobile sibling of the desktop
// ShareWatchlistModal (SE-5013). Rendered inside MobileSheetChrome (chrome
// anchors live in TEST_ID_MOBILE_SHEET_CHROME — ROOT/CLOSE/HANDLE/BODY/FOOTER),
// so this set only locates the sheet-specific content: the search input, the
// sticky selection-pill row, the scrollable ALL USERS list, the
// "Share with selection (N)" footer CTA, and the per-user row/radio/pill
// anchors. Drives the same select-and-diff sharing flow as the desktop modal
// via the shared useShareWatchlist hook.
export const TEST_ID_MOBILE_SHARE_SHEET = {
  USER_LIST: "MOBILE_SHARE_SHEET.USER_LIST",
};

// SE-5086: DesktopModal shared primitive — the desktop sibling of
// MobileSheetChrome (TEST_ID_MOBILE_SHEET_CHROME). Provides the shared Figma
// "Search Modal" chrome (484px md width, 24px padding, Heading-2 title
// top-left, 44×40 X close top-right, hardShadow:5 drop shadow, rounded-xl,
// bg-background-0, scrim backdrop). The 5 chrome anchors are hard-coded in the
// View so Playwright queries are stable across the SE-5013/5017/5021 consumers.
export const TEST_ID_DESKTOP_MODAL = {
  ROOT: "DESKTOP_MODAL.ROOT",
  CLOSE: "DESKTOP_MODAL.CLOSE",
  TITLE: "DESKTOP_MODAL.TITLE",
  BODY: "DESKTOP_MODAL.BODY",
  FOOTER: "DESKTOP_MODAL.FOOTER",
};

// SE-5087: DesktopDropdownMenu shared primitive — the dropdown-menu chrome that
// SE-5010 (list-header kebab), SE-5012 (row kebab), and SE-5015 (column config)
// all consume. ROOT anchors the menu surface; per-item anchors are keyed by the
// caller-supplied MenuItem.id so consumers can target individual rows.
export const TEST_ID_DESKTOP_DROPDOWN_MENU = {
  ROOT: "DESKTOP_DROPDOWN_MENU.ROOT",
  itemFor: (id: string): string => `DESKTOP_DROPDOWN_MENU.ITEM.${id}`,
};

// SE-5017 (SRD-9): AddPlayerDestinationModal — the Path B destination-selection
// modal opened from the Watchlists tab "Add player" button when "All Targets"
// is active. Anchors cover the modal root + title, the grouped My Lists /
// Shared-with-me section headers, each multi-select checkbox row, the inline
// "+ Create new list" row, the empty (zero-lists) state, and the disabled-until-
// selection footer submit button.
export const TEST_ID_ADD_PLAYER_DESTINATION = {
  ROOT: "ADD_PLAYER_DESTINATION.ROOT",
  TITLE: "ADD_PLAYER_DESTINATION.TITLE",
  SECTION_MY: "ADD_PLAYER_DESTINATION.SECTION.MY",
  SECTION_SHARED: "ADD_PLAYER_DESTINATION.SECTION.SHARED",
  CREATE_NEW_LIST: "ADD_PLAYER_DESTINATION.CREATE_NEW_LIST",
  EMPTY_STATE: "ADD_PLAYER_DESTINATION.EMPTY_STATE",
  SUBMIT_BTN: "ADD_PLAYER_DESTINATION.SUBMIT_BTN",
  CLOSE_BTN: "ADD_PLAYER_DESTINATION.CLOSE_BTN",
  rowFor: (id: string): string => `ADD_PLAYER_DESTINATION.ROW.${id}`,
  rowCheckboxFor: (id: string): string =>
    `ADD_PLAYER_DESTINATION.ROW.CHECKBOX.${id}`,
};

// SE-5504: the pipeline activity & audit trail. Two surfaces share ONE row
// vocabulary (`TEST_ID_PIPELINE_ACTIVITY`) so a spec asserts the same anchors
// whether it is looking at the card-scoped trail inside the player preview
// drawer or the whole-pipeline manager log:
//   - `rowFor(entryId)` — one audit entry;
//   - `autoMarkerFor(entryId)` — present ONLY on an entry whose actor is an
//     automation rule or the system, which is how the automated-vs-manual
//     distinction (AC2) is asserted;
//   - `actorFor` / `timestampFor` — the actor and timestamp AC3 requires.
//
// NOTE — the filter chips are anchored on TEST_ID_PIPELINE_ACTIVITY_LOG, not
// here. These anchors are shared by BOTH activity surfaces, and the chips
// belong only to the per-pipeline log: the per-card trail inside the player
// drawer is deliberately unfiltered (SE-6251 scope).
export const TEST_ID_PIPELINE_ACTIVITY = {
  EMPTY: "PIPELINE_ACTIVITY.EMPTY",
  ERROR: "PIPELINE_ACTIVITY.ERROR",
  LOADING: "PIPELINE_ACTIVITY.LOADING",
  LOAD_MORE: "PIPELINE_ACTIVITY.LOAD_MORE",
  rowFor: (entryId: string): string => `PIPELINE_ACTIVITY.ROW.${entryId}`,
  autoMarkerFor: (entryId: string): string =>
    `PIPELINE_ACTIVITY.AUTO_MARKER.${entryId}`,
  actorFor: (entryId: string): string => `PIPELINE_ACTIVITY.ACTOR.${entryId}`,
  timestampFor: (entryId: string): string =>
    `PIPELINE_ACTIVITY.TIMESTAMP.${entryId}`,
};

// SE-5504: the per-pipeline (manager-facing) activity log — its launcher in the
// board toolbar and the drawer it opens. Rows inside it carry the shared
// TEST_ID_PIPELINE_ACTIVITY anchors above.
//
// SE-6251 adds the filter chips: `FILTERS` anchors the chip region, and
// `chipFor(key)` addresses one chip by its catalogue key
// (`pipelineActivityFilters.ts`). Selection state is exposed through the chip's
// `accessibilityState.checked` — NOT `selected`. The chips render as
// `role="checkbox"` / `aria-checked`, because `aria-selected` on a button is
// not a valid ARIA pairing and RN-web does not expose it; see the CHIP_STATE
// note in `PipelineActivityLogDrawerView`. A spec reads them with
// `getByRole("checkbox", { checked })`, which is what a screen reader sees.
export const TEST_ID_PIPELINE_ACTIVITY_LOG = {
  OPEN: "PIPELINE_ACTIVITY_LOG.OPEN",
  DRAWER: "PIPELINE_ACTIVITY_LOG.DRAWER",
  CLOSE: "PIPELINE_ACTIVITY_LOG.CLOSE",
  TOTAL: "PIPELINE_ACTIVITY_LOG.TOTAL",
  FILTERS: "PIPELINE_ACTIVITY_LOG.FILTERS",
  chipFor: (key: string): string => `PIPELINE_ACTIVITY_LOG.CHIP.${key}`,
};

// SE-6980 [MR-3.1]: the quick-capture screen. Header chrome plus the 0-10
// score scale rows in the per-player evaluation panel.
//
// WEB TRAP for whoever writes the Playwright specs: `scaleFor(...)` lands on a
// Stack, which emits `data-testid` and so IS addressable in the DOM. The
// per-pill ids from `scalePillFor(...)` land on the Pressable inside
// SelectableChip, and the Pressable atom does NOT emit `data-testid` — those
// ids exist for jest/native only. On web, address a pill by its rendered text
// scoped inside the row, or by accessible name ("Performance 7").
//
// SAVE_STATE is a status region, not a button: it is the header's
// "All changes saved" / "Saved automatically" text, which the autosave hook
// drives. Asserting on it is how a spec proves autosave settled without
// racing the network.
export const TEST_ID_MATCH_REPORT_CAPTURE = {
  SCREEN: "MATCH_REPORT_CAPTURE.SCREEN",
  BACK: "MATCH_REPORT_CAPTURE.BACK",
  TITLE: "MATCH_REPORT_CAPTURE.TITLE",
  SUBTITLE: "MATCH_REPORT_CAPTURE.SUBTITLE",
  SAVE_STATE: "MATCH_REPORT_CAPTURE.SAVE_STATE",
  NOTED_COUNT: "MATCH_REPORT_CAPTURE.NOTED_COUNT",
  VIEW_TOGGLE: "MATCH_REPORT_CAPTURE.VIEW_TOGGLE",
  VIEW_TOGGLE_PITCH: "MATCH_REPORT_CAPTURE.VIEW_TOGGLE.PITCH",
  VIEW_TOGGLE_LIST: "MATCH_REPORT_CAPTURE.VIEW_TOGGLE.LIST",
  SUBMIT: "MATCH_REPORT_CAPTURE.SUBMIT",
  // The server's Partial/Complete verdict, rendered only after submit. A spec
  // asserting on this is asserting on what the SERVER decided, which is the
  // point: the client never computes completeness.
  STATUS: "MATCH_REPORT_CAPTURE.STATUS",
  SUBMIT_CONFIRM: "MATCH_REPORT_CAPTURE.SUBMIT_CONFIRM",
  /**
   * The explaining label above a score scale (SE-7551).
   *
   * Separate from `scaleFor`, which is the row: the label is what carries the
   * tooltip, and a spec that wants to prove the explanation exists has to name
   * the thing that explains rather than the eleven pills beneath it.
   */
  metricLabelFor: (field: "performance" | "potential"): string =>
    `MATCH_REPORT_CAPTURE.METRIC_LABEL.${field}`,
  scaleFor: (field: "performance" | "potential"): string =>
    `MATCH_REPORT_CAPTURE.SCALE.${field}`,
  scalePillFor: (field: "performance" | "potential", score: number): string =>
    `MATCH_REPORT_CAPTURE.SCALE.${field}.${score}`,

  // The per-player evaluation panel and the report-level watch mode.
  //
  // `playerRowFor` and `panelFor` are keyed by player id on purpose. AC2 is
  // that one player's note never appears on another, and an id-keyed selector
  // is what lets a spec assert that positively — open B, assert B's panel
  // holds B's text — rather than settling for "the note field changed".
  squadLoadingFor: (teamId: string): string =>
    `MATCH_REPORT_CAPTURE.SQUAD_LOADING.${teamId}`,
  HEADER_LOADING: "MATCH_REPORT_CAPTURE.HEADER_LOADING",
  PANEL: "MATCH_REPORT_CAPTURE.PANEL",
  PANEL_CLOSE: "MATCH_REPORT_CAPTURE.PANEL.CLOSE",
  // SE-7550 submit confirmation. The partial note is keyed separately from the
  // summary because its ABSENCE is the assertion on a complete report — a
  // spec that could only find the summary would pass either way.
  SUBMIT_CONFIRM_BLURB: "MATCH_REPORT_CAPTURE.SUBMIT_CONFIRM.BLURB",
  SUBMIT_CONFIRM_SUMMARY: "MATCH_REPORT_CAPTURE.SUBMIT_CONFIRM.SUMMARY",
  SUBMIT_CONFIRM_PARTIAL_NOTE:
    "MATCH_REPORT_CAPTURE.SUBMIT_CONFIRM.PARTIAL_NOTE",
  submitConfirmRowFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.SUBMIT_CONFIRM.ROW.${playerId}`,
  // SE-7716: the one place the unset-vs-0 rule is stated — a scale-level
  // hint below the Performance/Potential pair, deliberately NOT inside
  // either metric tooltip (see metricHints.ts).
  SCALE_UNSET_HINT: "MATCH_REPORT_CAPTURE.SCALE.UNSET_HINT",
  PANEL_NOTE: "MATCH_REPORT_CAPTURE.PANEL.NOTE",
  PANEL_STARTER: "MATCH_REPORT_CAPTURE.PANEL.STARTER",
  PANEL_PLAYER_PAGE: "MATCH_REPORT_CAPTURE.PANEL.PLAYER_PAGE",
  WATCH_MODE: "MATCH_REPORT_CAPTURE.WATCH_MODE",
  WATCH_MODE_LIVE: "MATCH_REPORT_CAPTURE.WATCH_MODE.LIVE",
  WATCH_MODE_VIDEO: "MATCH_REPORT_CAPTURE.WATCH_MODE.VIDEO",
  playerRowFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.PLAYER.${playerId}`,
  panelFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.PANEL.${playerId}`,

  // SE-6981 add-player. Keyed by TEAM, because there is one drawer per squad
  // column and which column the scout typed into is the whole difference
  // between adding a substitute to the right side and to the wrong one.
  addPlayerFor: (teamId: string): string =>
    `MATCH_REPORT_CAPTURE.ADD_PLAYER.${teamId}`,
  addPlayerSearchFor: (teamId: string): string =>
    `MATCH_REPORT_CAPTURE.ADD_PLAYER.SEARCH.${teamId}`,
  // Keyed by player id so a spec can assert it added the player it MEANT to,
  // rather than "some row was pressed and the count went up".
  addPlayerResultFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.ADD_PLAYER.RESULT.${playerId}`,
  // The "New" mark on a player added during this capture. Distinct from the
  // app-wide MANUALLY ADDED badge: that one means "this record was hand-typed
  // rather than ingested", which is a property of the PLAYER. This means "this
  // player was not in the squad the server handed us", which is a property of
  // THIS REPORT — and it is true of an existing base player added via search,
  // who is not manual at all.
  newInSquadFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.NEW_IN_SQUAD.${playerId}`,
  // The suspension mark (SE-7018). Keyed by player id so a spec asserts the
  // mark landed on the player the signal named, rather than "a warning is on
  // screen somewhere" — which on a two-squad picker is nearly free to satisfy
  // and says almost nothing.
  suspensionWarningFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.SUSPENSION_WARNING.${playerId}`,
  // SE-7803 — the noted check, and the Performance / Potential scores beside
  // it, on a capture squad row.
  //
  // Their own `ROW_NOTED.` / `ROW_SCORE.` namespaces rather than anything
  // under `PLAYER.`, for the reason `SAVE_STATE_PANEL` records above:
  // `playerRowFor` mints ids at `…PLAYER.<id>`, so a spec sweeping that prefix
  // to count rows would start counting marks INSIDE a row and read the
  // arithmetic change as a data problem.
  //
  // Keyed by player id, and the score also by field, because the claim worth
  // making is "THIS player's Performance reads 8" — "a score is on screen" is
  // nearly free to satisfy on a two-squad column and says almost nothing.
  notedCheckFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.ROW_NOTED.${playerId}`,
  rowScoreFor: (field: "performance" | "potential", playerId: string): string =>
    `MATCH_REPORT_CAPTURE.ROW_SCORE.${field}.${playerId}`,
  // SE-7802 — the inline line-up switch on a capture squad row.
  //
  // Its own `ROW_STARTER.` namespace for the same reason as `ROW_NOTED.` and
  // `ROW_SCORE.` above: `playerRowFor` mints ids at `…PLAYER.<id>`, so a spec
  // sweeping that prefix to count rows would start counting switches inside
  // rows and read the arithmetic change as a data problem.
  //
  // NOT under `PANEL.` either, though it renders the same control: the panel's
  // switch is one per screen and this is one per row. A spec asserting on
  // `PANEL_STARTER` must keep meaning "the open editor's switch", or the two
  // surfaces this ticket deliberately keeps in step stop being separable in
  // the assertion that proves they are.
  starterSwitchFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.ROW_STARTER.${playerId}`,
  // The responder claim WRAPPING that switch, which is what stops a mark from
  // also opening the player's editor.
  //
  // It carries an id of its own because the guard is not unit-testable through
  // behaviour: RNTL's `fireEvent(node, "valueChange")` calls the handler
  // directly and never traverses the responder chain, so "fire the switch and
  // assert the row did not open" passes whether the guard is there or not —
  // measured by deleting it. Walking up from the switch does not work either,
  // because the row's own `Pressable` sets responder props, so any walk that
  // reaches it reports success for the wrong element. An id on the guard is
  // the one handle that fails when the guard goes.
  starterSwitchGuardFor: (playerId: string): string =>
    `MATCH_REPORT_CAPTURE.ROW_STARTER_GUARD.${playerId}`,
  ADD_PLAYER_UNDO: "MATCH_REPORT_CAPTURE.ADD_PLAYER.UNDO",
  LOAD_ERROR: "MATCH_REPORT_CAPTURE.LOAD_ERROR",

  // SE-6983 offline capture.
  //
  // The panel's save line is `SAVE_STATE.PANEL` rather than `PANEL.SAVE_STATE`
  // deliberately. `panelFor(playerId)` mints ids under `…PANEL.<id>`, so a
  // spec that counts or sweeps the `PANEL.` namespace to find open panels
  // would start matching a save label instead of a player — a new id changing
  // an unrelated spec's arithmetic, which reads as a data problem rather than
  // as the naming collision it is. Save state is its own namespace, and the
  // header's existing `SAVE_STATE` is its sibling.
  SAVE_STATE_PANEL: "MATCH_REPORT_CAPTURE.SAVE_STATE.PANEL",
  OFFLINE_BANNER: "MATCH_REPORT_CAPTURE.OFFLINE_BANNER",
  DRAFT_PILL: "MATCH_REPORT_CAPTURE.DRAFT_PILL",
  SYNC_FAILED_TOAST: "MATCH_REPORT_CAPTURE.SYNC_FAILED_TOAST",
  SYNC_RETRY: "MATCH_REPORT_CAPTURE.SYNC_RETRY",
  // One per team. Without these a spec can only say "some unnoted row", which
  // cannot express "one player from EACH squad" — the claim the PRD journey
  // wants to make and currently cannot, because every dev fixture has an empty
  // home squad.
  squadColumnFor: (teamId: string): string =>
    `MATCH_REPORT_CAPTURE.SQUAD_COLUMN.${teamId}`,

  // SE-7801 — the STARTERS / SQUAD sections the frame draws and SE-7551
  // dismissed as already existing.
  //
  // Their own `SECTION.` namespace rather than anything under `PANEL.` or
  // `SQUAD_COLUMN.`, for the reason `SAVE_STATE_PANEL` records above: ids
  // minted under a prefix another selector sweeps start answering that
  // selector's question. `squadColumnFor` is swept per team, and a section id
  // nested beneath it would join that column's count.
  //
  // Keyed by TEAM as well as group because there are two of each on screen,
  // and "a STARTERS header is visible" is nearly free to satisfy on a
  // two-squad layout — it says nothing about which squad grouped correctly.
  sectionFor: (teamId: string, group: "starters" | "squad"): string =>
    `MATCH_REPORT_CAPTURE.SECTION.${teamId}.${group}`,
  sectionToggleFor: (teamId: string, group: "starters" | "squad"): string =>
    `MATCH_REPORT_CAPTURE.SECTION_TOGGLE.${teamId}.${group}`,
  // The line that says WHY STARTERS is empty. Asserted positively so "the
  // section rendered with nothing in it" and "the section explained itself"
  // stay distinguishable — the first is the bug the second exists to prevent.
  startersEmptyFor: (teamId: string): string =>
    `MATCH_REPORT_CAPTURE.STARTERS_EMPTY.${teamId}`,
};

/**
 * Fixture Planning (SE-6975 / 6976 / 6977), Figma frames 18, 22, 22b, 23.
 *
 * The row and day helpers are keyed by ids the SERVER minted, so a spec never
 * has to guess an index — and a row that moved because a sort or filter changed
 * still resolves to the same fixture.
 */
export const TEST_ID_FIXTURE_PLANNING = {
  SCREEN: "FIXTURE_PLANNING.SCREEN",
  ERROR: "FIXTURE_PLANNING.ERROR",
  MONTH: "FIXTURE_PLANNING.MONTH",
  STATS: "FIXTURE_PLANNING.STATS",
  FILTERS: "FIXTURE_PLANNING.FILTERS",
  CALENDAR: "FIXTURE_PLANNING.CALENDAR",
  LIST: "FIXTURE_PLANNING.LIST",
  /**
   * The Calendar / List segmented toggle in the Matches header (SE-7553).
   *
   * Deliberately NOT `FIXTURE_PLANNING.CALENDAR` / `.LIST` with a suffix: those
   * two are the BODIES this toggle chooses between, and reusing their stems
   * would make "is the list showing?" and "is the List segment on?" the same
   * question to a prefix matcher. They are different questions — the whole
   * point of persisting the choice is that one can be true while the surface
   * is not even mounted.
   */
  VIEW_TOGGLE: "FIXTURE_PLANNING.VIEW_TOGGLE",
  VIEW_CALENDAR: "FIXTURE_PLANNING.VIEW.CALENDAR",
  VIEW_LIST: "FIXTURE_PLANNING.VIEW.LIST",
  ADD_MATCH: "FIXTURE_PLANNING.ADD_MATCH",
  ASSIGN_POPOVER: "FIXTURE_PLANNING.ASSIGN_POPOVER",
  ASSIGN_DONE: "FIXTURE_PLANNING.ASSIGN_DONE",
  ADD_MATCH_MODAL: "FIXTURE_PLANNING.ADD_MATCH_MODAL",
  ADD_MATCH_SAVE: "FIXTURE_PLANNING.ADD_MATCH_SAVE",
  ADD_MATCH_CREATE_TEAM: "FIXTURE_PLANNING.ADD_MATCH_CREATE_TEAM",
  UNASSIGNED_STAT: "FIXTURE_PLANNING.STAT.UNASSIGNED",
  /**
   * Stems, not element ids.
   *
   * These filters render through the shared `FilterChip`, which builds a
   * trigger id and one id per option off a single stem. `chipTriggerFor` /
   * `chipOptionFor` below are how a spec addresses either — spelling the
   * suffix out at a call site is how the two halves drift apart.
   */
  COMPETITION_FILTER: "FIXTURE_PLANNING.FILTER.COMPETITION",
  SCOUT_FILTER: "FIXTURE_PLANNING.FILTER.SCOUT",
  ADD_MATCH_HOME_TEAM: "FIXTURE_PLANNING.ADD_MATCH.HOME_TEAM",
  ADD_MATCH_AWAY_TEAM: "FIXTURE_PLANNING.ADD_MATCH.AWAY_TEAM",
  ADD_MATCH_COMPETITION: "FIXTURE_PLANNING.ADD_MATCH.COMPETITION",
  DATE_CHIP: "FIXTURE_PLANNING.FILTER.DATE",
  SHOWN_COUNT: "FIXTURE_PLANNING.FILTER.SHOWN_COUNT",
  SHOWN_COUNT_VALUE: "FIXTURE_PLANNING.FILTER.SHOWN_COUNT_VALUE",
  /** Jumps the calendar back to the current month. */
  TODAY: "FIXTURE_PLANNING.TODAY",
  FILTER_SEARCH: "FIXTURE_PLANNING.FILTER.SEARCH",
  FILTER_SEARCH_CLEAR: "FIXTURE_PLANNING.FILTER.SEARCH_CLEAR",
  /**
   * The in-flight overlay that sits ON the calendar/list.
   *
   * Deliberately outside the `FILTER.` namespace: it covers the content
   * region, not the filter row, and an id that reads as a filter would send
   * the next reader looking for it in the wrong component.
   */
  LOADING: "FIXTURE_PLANNING.LOADING",
  chipTriggerFor: (stem: string): string => `${stem}-trigger`,
  chipOptionFor: (stem: string, optionId: string): string =>
    `${stem}-option:${optionId}`,
  /**
   * The chip's clear-the-filter row.
   *
   * Outside the `-option:` namespace on purpose — suites count the real
   * options by that prefix, and a clear row inside it would add one to every
   * such count.
   */
  chipClearFor: (stem: string): string => `${stem}-clear-all`,
  timeframeFor: (timeframe: string): string =>
    `FIXTURE_PLANNING.FILTER.TIMEFRAME.${timeframe}`,
  rowFor: (matchId: string): string => `FIXTURE_PLANNING.ROW.${matchId}`,
  assignFor: (matchId: string): string => `FIXTURE_PLANNING.ASSIGN.${matchId}`,
  dayFor: (iso: string): string => `FIXTURE_PLANNING.DAY.${iso}`,
  calendarChipFor: (matchId: string): string =>
    `FIXTURE_PLANNING.CALENDAR_CHIP.${matchId}`,
  dayOverflowFor: (iso: string): string =>
    `FIXTURE_PLANNING.DAY_OVERFLOW.${iso}`,
  scoutOptionFor: (userId: string): string =>
    `FIXTURE_PLANNING.SCOUT.${userId}`,
};

/**
 * The match detail page (SE-6978), Figma frame 25.
 */
export const TEST_ID_MATCH_DETAIL = {
  SCREEN: "MATCH_DETAIL.SCREEN",
  HEADER: "MATCH_DETAIL.HEADER",
  CAPTURE: "MATCH_DETAIL.CAPTURE",
  SQUADS: "MATCH_DETAIL.SQUADS",
  SCOUT_COVERAGE: "MATCH_DETAIL.SCOUT_COVERAGE",
  REPORTS: "MATCH_DETAIL.REPORTS",
  EVENTS_ABSENT: "MATCH_DETAIL.EVENTS_ABSENT",
  MISSING: "MATCH_DETAIL.MISSING",
  tabFor: (tab: string): string => `MATCH_DETAIL.TAB.${tab}`,
  reportFor: (reportId: string): string => `MATCH_DETAIL.REPORT.${reportId}`,
};

/**
 * Match events, entered by hand (SE-7293).
 *
 * `EVENTS_ABSENT` above is the tab's empty state and stays: an empty fixture is
 * a real state, not a missing feature.
 */
export const TEST_ID_MATCH_EVENTS = {
  PANEL: "MATCH_EVENTS.PANEL",
  LIST: "MATCH_EVENTS.LIST",
  /** The column-heading row above the events (SE-7997). */
  HEADER: "MATCH_EVENTS.HEADER",
  ADD: "MATCH_EVENTS.ADD",
  FORM: "MATCH_EVENTS.FORM",
  FORM_ERROR: "MATCH_EVENTS.FORM_ERROR",
  PLAYER_SELECT: "MATCH_EVENTS.PLAYER_SELECT",
  MINUTE_INPUT: "MATCH_EVENTS.MINUTE_INPUT",
  OWN_GOAL_HINT: "MATCH_EVENTS.OWN_GOAL_HINT",
  SAVE: "MATCH_EVENTS.SAVE",
  SAVE_AND_ADD: "MATCH_EVENTS.SAVE_AND_ADD",
  DELETE: "MATCH_EVENTS.DELETE",
  CANCEL: "MATCH_EVENTS.CANCEL",
  LOADING: "MATCH_EVENTS.LOADING",
  ERROR: "MATCH_EVENTS.ERROR",
  kindOption: (kind: string): string => `MATCH_EVENTS.KIND.${kind}`,
  teamOption: (teamId: string): string => `MATCH_EVENTS.TEAM.${teamId}`,
  rowFor: (eventId: string): string => `MATCH_EVENTS.ROW.${eventId}`,
};

/**
 * Minutes played per player (SE-7293).
 *
 * A DURATION ledger, not a line-up: no starter flag, no position, no shirt
 * number. Named apart from MATCH_EVENTS because the two are different facts
 * about a fixture, not two views of one.
 */
export const TEST_ID_MATCH_APPEARANCES = {
  PANEL: "MATCH_APPEARANCES.PANEL",
  LIST: "MATCH_APPEARANCES.LIST",
  EMPTY: "MATCH_APPEARANCES.EMPTY",
  LOADING: "MATCH_APPEARANCES.LOADING",
  ERROR: "MATCH_APPEARANCES.ERROR",
  WRITE_ERROR: "MATCH_APPEARANCES.WRITE_ERROR",
  PLAYER_SELECT: "MATCH_APPEARANCES.PLAYER_SELECT",
  MINUTES_INPUT: "MATCH_APPEARANCES.MINUTES_INPUT",
  ALL_RECORDED: "MATCH_APPEARANCES.ALL_RECORDED",
  NO_PLAYERS: "MATCH_APPEARANCES.NO_PLAYERS",
  SAVE: "MATCH_APPEARANCES.SAVE",
  DELETE: "MATCH_APPEARANCES.DELETE",
  CANCEL_EDIT: "MATCH_APPEARANCES.CANCEL_EDIT",
  /**
   * The entry form's own card (SE-8012).
   *
   * Its own stem rather than a child of PANEL or SAVE: an id nested inside
   * another id's prefix silently changes what every `^=` selector on that
   * prefix counts.
   */
  FORM: "MATCH_APPEARANCES.FORM",
  /** What the disabled submit is still waiting for (SE-8012). */
  REQUIREMENT: "MATCH_APPEARANCES.REQUIREMENT",
  teamOption: (teamId: string): string => `MATCH_APPEARANCES.TEAM.${teamId}`,
  rowFor: (appearanceId: string): string =>
    `MATCH_APPEARANCES.ROW.${appearanceId}`,
};

/**
 * The mobile capture flow (SE-6987), frames 30-33.
 */
export const TEST_ID_MOBILE_CAPTURE = {
  HEADER: "MOBILE_CAPTURE.HEADER",
  NOTED_COUNT: "MOBILE_CAPTURE.NOTED_COUNT",
  // SE-7286 AC#3. The phone header had no completeness verdict at all — the
  // desktop toolbar's pill has no counterpart here — so a scout who submitted
  // a report on a phone was never told whether it filed Partial or Complete.
  // Measured on an S24 against a report the API reported PARTIAL.
  STATUS: "MOBILE_CAPTURE.STATUS",
  SUBMIT: "MOBILE_CAPTURE.SUBMIT",
  PICKER: "MOBILE_CAPTURE.PICKER",
  START_NOTING: "MOBILE_CAPTURE.START_NOTING",
  // SE-7818 — switches what the picker's checkboxes MEAN: "I will note this
  // player" or "this player started".
  //
  // One id for the control in both directions, because it is one control that
  // reads "Mark line-up" or "Done". A spec asserting the mode should read the
  // header sentence beside it rather than this button's label — the label
  // names the ACTION, and the sentence names the STATE, and a test that
  // confuses them passes whichever way the toggle happens to be pointing.
  LINEUP_MODE_TOGGLE: "MOBILE_CAPTURE.LINEUP_MODE_TOGGLE",
  PROGRESS: "MOBILE_CAPTURE.PROGRESS",
  PROGRESS_BAR: "MOBILE_CAPTURE.PROGRESS_BAR",
  /** The step-through identity card — who the open step is about (SE-7550). */
  PLAYER_CARD: "MOBILE_CAPTURE.PLAYER_CARD",
  SKIP: "MOBILE_CAPTURE.SKIP",
  NEXT: "MOBILE_CAPTURE.NEXT",
  ADD_PLAYER_SHEET: "MOBILE_CAPTURE.ADD_PLAYER_SHEET",
  // SE-7282. The sticky footers carry each phase's only action and are the
  // elements the navigation bar was eating, so they need identities of their
  // own — a test can then assert the safe-area padding actually reached them,
  // rather than only that the screen still renders.
  PICK_FOOTER: "MOBILE_CAPTURE.PICK_FOOTER",
  STEP_FOOTER: "MOBILE_CAPTURE.STEP_FOOTER",
  OPEN_ADD_PLAYER: "MOBILE_CAPTURE.OPEN_ADD_PLAYER",
  tabFor: (team: string): string => `MOBILE_CAPTURE.TAB.${team}`,
  selectAllFor: (team: string): string => `MOBILE_CAPTURE.SELECT_ALL.${team}`,
  pickFor: (playerId: string): string => `MOBILE_CAPTURE.PICK.${playerId}`,
};

/**
 * The phone's My fixtures list (SE-6987), frame 30.
 */
export const TEST_ID_MY_FIXTURES = {
  SCREEN: "MY_FIXTURES.SCREEN",
  /**
   * The scrolling card region. Where `ADD_MATCH` sits is decided against it
   * (SE-8492): inside, after `EMPTY`, when the segment has no rows; pinned
   * after it otherwise.
   */
  LIST: "MY_FIXTURES.LIST",
  /** The per-segment empty copy. */
  EMPTY: "MY_FIXTURES.EMPTY",
  ADD_MATCH: "MY_FIXTURES.ADD_MATCH",
  segmentFor: (segment: string): string => `MY_FIXTURES.SEGMENT.${segment}`,
  cardFor: (matchId: string): string => `MY_FIXTURES.CARD.${matchId}`,
  /**
   * The card's own primary action — "Start capture" / "Continue draft" /
   * "View report" (SE-7715).
   *
   * The card was addressable and its CTA was not, which is why a card clipped
   * at 120dp still satisfied every automated surface: `MY_FIXTURES.ADD_MATCH`
   * is the SCREEN's footer, so asserting it says nothing about whether a
   * card's own button ever rendered. On the S24 it did not.
   */
  captureFor: (matchId: string): string => `MY_FIXTURES.CAPTURE.${matchId}`,
};

/**
 * The match-reports section of a player's page (SE-6986), Figma frame 26.
 */
export const TEST_ID_PLAYER_MATCH_REPORTS = {
  SECTION: "PLAYER_MATCH_REPORTS.SECTION",
  MATCHES_WATCHED: "PLAYER_MATCH_REPORTS.MATCHES_WATCHED",
  /** Opens the pick-pipeline-and-stage dialog for this player (SE-7214). */
  PROMOTE: "PLAYER_MATCH_REPORTS.PROMOTE",
  /** The two average cards and the coverage line each discloses (SE-7237). */
  AVG_PERFORMANCE: "PLAYER_MATCH_REPORTS.AVG_PERFORMANCE",
  AVG_POTENTIAL: "PLAYER_MATCH_REPORTS.AVG_POTENTIAL",
  PERFORMANCE_COVERAGE: "PLAYER_MATCH_REPORTS.PERFORMANCE_COVERAGE",
  POTENTIAL_COVERAGE: "PLAYER_MATCH_REPORTS.POTENTIAL_COVERAGE",
  /** The list card the fixture rows sit in, and its ownership caption (SE-8016). */
  LIST: "PLAYER_MATCH_REPORTS.LIST",
  OWNERSHIP_NOTE: "PLAYER_MATCH_REPORTS.OWNERSHIP_NOTE",
  rowFor: (matchId: string): string => `PLAYER_MATCH_REPORTS.ROW.${matchId}`,
  /** The labelled Perf/Pot pair on a collapsed fixture row (SE-7237). */
  scoresFor: (matchId: string): string =>
    `PLAYER_MATCH_REPORTS.ROW_SCORES.${matchId}`,
  /**
   * The scout's own column on a collapsed row (SE-8016).
   *
   * Addressable because "the scout is named" is an assertion a test has to be
   * able to make about a CELL: the name also appears inside the expanded
   * panel's prose, so a bare text lookup passes on the panel while the column
   * the ticket is about renders blank.
   *
   * Its own stem — `SCOUT_CELL`, not `ROW_…` — on purpose. A spec counts the
   * rows with the prefix locator `^="PLAYER_MATCH_REPORTS.ROW."`, and every
   * child id minted under a parent's stem is one dot away from being counted
   * as another row (SE-8049 is the second instance of that hazard found this
   * week). A sibling stem cannot collide however the locator is later loosened.
   */
  scoutFor: (matchId: string): string =>
    `PLAYER_MATCH_REPORTS.SCOUT_CELL.${matchId}`,
  /**
   * The expanded panel's scope callout (SE-8016).
   *
   * Its ABSENCE is an assertion: a report covering only this player has no
   * other players to withhold, and the callout is withheld rather than reading
   * "the other 0 players".
   */
  scopeCalloutFor: (matchId: string): string =>
    `PLAYER_MATCH_REPORTS.SCOPE_CALLOUT.${matchId}`,
  /** One additional scout's read inside the expanded panel (SE-8016). */
  secondScoutFor: (evaluationId: string): string =>
    `PLAYER_MATCH_REPORTS.SECOND_SCOUT.${evaluationId}`,
};

/**
 * The player identity header on the standalone reports route (SE-7237).
 *
 * `/players/{id}/reports` renders the reports feature on its own, so without
 * this header the screen is a page of numbers about an unnamed subject.
 */
export const TEST_ID_PLAYER_REPORTS_IDENTITY = {
  HEADER: "PLAYER_REPORTS_IDENTITY.HEADER",
  NAME: "PLAYER_REPORTS_IDENTITY.NAME",
  BACK: "PLAYER_REPORTS_IDENTITY.BACK",
};

/**
 * The Financial & Compliance dashboard (FC epic, SE-7005 page shell).
 *
 * The `*_SLOT` ids name composition points the page shell renders but does not
 * fill: the insights feed, the compliance layers section and the scenario
 * overlay banner are owned by sibling tickets. They are declared here so a
 * sibling adds content to a slot rather than a new id to this file.
 */
export const TEST_ID_FIN_COMPLIANCE = {
  SCREEN: "FIN_COMPLIANCE.SCREEN",
  GATE_SPINNER: "FIN_COMPLIANCE.GATE_SPINNER",
  TITLE_BAR: "FIN_COMPLIANCE.TITLE_BAR",
  SCOPE_SELECT: "FIN_COMPLIANCE.SCOPE_SELECT",
  PERIOD_SELECT: "FIN_COMPLIANCE.PERIOD_SELECT",
  // The open option panels. Both selectors echo the selected option's label on
  // their own trigger, so "Current squad" is on screen twice while a panel is
  // open — scoping an option lookup to its panel is what keeps a locator from
  // matching the trigger it was opened from.
  SCOPE_PANEL: "FIN_COMPLIANCE.SCOPE_PANEL",
  PERIOD_PANEL: "FIN_COMPLIANCE.PERIOD_PANEL",
  COMPARISON: "FIN_COMPLIANCE.COMPARISON",
  // SE-7227. The non-blocking line accounting for a scope list that is shorter
  // than the reader expects. Present ONLY when the scenario read failed — an
  // empty-but-permitted list is a correct answer and says nothing (SE-6820), so
  // this id's absence is itself an assertion other specs rely on.
  SCOPE_NOTICE: "FIN_COMPLIANCE.SCOPE_NOTICE",
  // SE-7446. The line accounting for a deep link that named a scenario this
  // page could not land on. Deliberately NOT `SCOPE_NOTICE`: that id's absence
  // asserts "the scenario read succeeded" and specs rely on it, so a second
  // meaning would silently weaken those. Present ONLY for a SETTLED miss — a
  // link still resolving says nothing, and no link at all says nothing.
  //
  // Its TEXT is uniform across every reason a link can fail (deleted, never
  // existed, someone else's), so a spec may assert the id and the copy but must
  // never expect the copy to name a cause — there isn't one to name, by design.
  SCENARIO_LINK_NOTICE: "FIN_COMPLIANCE.SCENARIO_LINK_NOTICE",
  // SE-8397. Unconditional whenever `isProjection` is true — the scenario
  // projection's figures are never real yet, same reasoning as the insights
  // insights feed's own placeholder badge, which SE-8464 removed along
  // with the fixture fallback it warned about.
  SCENARIO_COMING_SOON: "FIN_COMPLIANCE.SCENARIO_COMING_SOON",
  MANAGE_OBJECTIVES: "FIN_COMPLIANCE.MANAGE_OBJECTIVES",
  HIDE_TRENDS: "FIN_COMPLIANCE.HIDE_TRENDS",
  BREACH_CHIP: "FIN_COMPLIANCE.BREACH_CHIP",
  GRID: "FIN_COMPLIANCE.GRID",
  ADD_METRIC: "FIN_COMPLIANCE.ADD_METRIC",
  EMPTY_STATE: "FIN_COMPLIANCE.EMPTY_STATE",
  PARTIAL_DATA: "FIN_COMPLIANCE.PARTIAL_DATA",
  INSIGHTS_SLOT: "FIN_COMPLIANCE.INSIGHTS_SLOT",
  LAYERS_SLOT: "FIN_COMPLIANCE.LAYERS_SLOT",
  SCENARIO_OVERLAY_SLOT: "FIN_COMPLIANCE.SCENARIO_OVERLAY_SLOT",
  scopeOptionFor: (scopeId: string): string =>
    `FIN_COMPLIANCE.SCOPE_OPTION.${scopeId}`,
  periodOptionFor: (periodId: string): string =>
    `FIN_COMPLIANCE.PERIOD_OPTION.${periodId}`,
  cardFor: (kpiId: string): string => `FIN_COMPLIANCE.CARD.${kpiId}`,
  valueFor: (kpiId: string): string => `FIN_COMPLIANCE.VALUE.${kpiId}`,
  targetFor: (kpiId: string): string => `FIN_COMPLIANCE.TARGET.${kpiId}`,
  deviationFor: (kpiId: string): string => `FIN_COMPLIANCE.DEVIATION.${kpiId}`,
  stateDotFor: (kpiId: string): string => `FIN_COMPLIANCE.STATE_DOT.${kpiId}`,
  sparklineFor: (kpiId: string): string => `FIN_COMPLIANCE.SPARKLINE.${kpiId}`,
  trendToggleFor: (kpiId: string): string =>
    `FIN_COMPLIANCE.TREND_TOGGLE.${kpiId}`,
  // SE-8350. Present only on a card whose trend-line preference is an
  // explicit override rather than the source's default — see
  // `hasExplicitTrendOverride` on `ComplianceKpi`.
  resetTrendFor: (kpiId: string): string =>
    `FIN_COMPLIANCE.RESET_TREND.${kpiId}`,
  editFor: (kpiId: string): string => `FIN_COMPLIANCE.EDIT.${kpiId}`,
  drillFor: (kpiId: string): string => `FIN_COMPLIANCE.DRILL.${kpiId}`,
  // SE-8381. The supplementary line under a lower-bound value's headline
  // figure — the exclusions it names, a denominator-fallback disclosure, or
  // both. Present only when `KpiMeasuredReading.noteLine` is non-null.
  noteFor: (kpiId: string): string => `FIN_COMPLIANCE.NOTE.${kpiId}`,
  // SE-8422. Present only on a fixed-slot regime-rule card whose real
  // evaluation was resolved — see `regimeLayerQualifier` on `ComplianceKpi`.
  regimeQualifierFor: (kpiId: string): string =>
    `FIN_COMPLIANCE.REGIME_QUALIFIER.${kpiId}`,
};

/**
 * Plan Status and Objectives tiles on the Squad Planning KPI row (SE-7003).
 *
 * A namespace of its own rather than more keys on `TEST_ID_FIN_COMPLIANCE`:
 * the tiles render on the Squad Planning tab, not the Compliance one, and an
 * id here should not read as "part of the KPI grid". Both tiles are ABSENT
 * from the DOM — not hidden — when the payload carries nothing for them
 * (zero rules; no INTERNAL layer), so `toHaveCount(0)` on `TILE` asserts the
 * payload, which is why each such assertion is paired with a positive taken
 * from the same render (TOTAL PLAYERS is always there).
 */
export const TEST_ID_PLAN_STATUS = {
  TILE: "PLAN_STATUS.TILE",
  LABEL: "PLAN_STATUS.LABEL",
  HEADLINE: "PLAN_STATUS.HEADLINE",
  // The "Unsaved · save to re-evaluate" line, present only while the planning
  // draft holds changes the server has not yet evaluated.
  PENDING: "PLAN_STATUS.PENDING",
  OBJECTIVES_TILE: "PLAN_STATUS.OBJECTIVES",
  OBJECTIVES_PILL: "PLAN_STATUS.OBJECTIVES_PILL",
};

/**
 * The "Manage targets & rules" dialog (SE-7009).
 *
 * A namespace of its own rather than more keys on `TEST_ID_FIN_COMPLIANCE`:
 * this is a modal transaction over the RULES behind the dashboard, and an id
 * here should not read as "part of the KPI grid" it is opened from.
 *
 * The dialog has exactly TWO tabs — Continental and Domestic. Internal
 * objectives are configured in Planning's Club objectives modal (SE-7004), so
 * there is deliberately no `INTERNAL` tab id to reach for here.
 */
export const TEST_ID_FIN_TARGETS = {
  DIALOG: "FIN_TARGETS.DIALOG",
  TITLE: "FIN_TARGETS.TITLE",
  SUBTITLE: "FIN_TARGETS.SUBTITLE",
  TABS: "FIN_TARGETS.TABS",
  // No ✕ id: the ratified frames draw no header dismiss on this dialog — the
  // footer's Cancel is the whole of it — and an id for a control the design
  // does not have would invite one being added to satisfy the id.
  CANCEL: "FIN_TARGETS.CANCEL",
  SAVE: "FIN_TARGETS.SAVE",
  // SE-7835: a rejected real save (e.g. CAP dropped below AMBER) used to
  // leave the dialog silently stuck — this is what tells the reader why.
  SAVE_ERROR: "FIN_TARGETS.SAVE_ERROR",
  /** One layer tab, keyed by layer id. */
  tabFor: (layerId: string): string => `FIN_TARGETS.TAB.${layerId}`,
  /** One rule row, keyed by rule id. */
  ruleFor: (ruleId: string): string => `FIN_TARGETS.RULE.${ruleId}`,
  /** A rule's "Show KPI" switch, keyed by rule id. */
  showKpiFor: (ruleId: string): string => `FIN_TARGETS.SHOW_KPI.${ruleId}`,
  /** An editable threshold box, keyed by rule id then field id. */
  fieldFor: (ruleId: string, fieldId: string): string =>
    `FIN_TARGETS.FIELD.${ruleId}.${fieldId}`,
  /**
   * The same threshold rendered as TEXT for a reader who may not edit.
   *
   * A separate id from the editable box on purpose, exactly as the Edit dialog
   * does it: "this reader gets read-only values" is proved by this id being
   * present while `fieldFor` is absent, which a shared id could not express.
   */
  valueFor: (ruleId: string, fieldId: string): string =>
    `FIN_TARGETS.VALUE.${ruleId}.${fieldId}`,
  /**
   * The note shown under a rule whose Show KPI switch is locked, keyed by
   * rule id (SE-7939).
   */
  helperFor: (ruleId: string): string => `FIN_TARGETS.HELPER.${ruleId}`,
  /**
   * The note under a row whose rule the club's league does not have
   * (SE-8454), keyed by rule id.
   *
   * Separate from {@link helperFor} deliberately: that one explains a locked
   * Show KPI SWITCH, this one explains greyed THRESHOLD boxes. Sharing an id
   * would make a test that found the note unable to say which of the two
   * states it had actually found.
   */
  unbackedFor: (ruleId: string): string => `FIN_TARGETS.UNBACKED.${ruleId}`,
  /**
   * A threshold box greyed because the club's league has no such rule
   * (SE-8454), keyed by rule id then field id.
   *
   * A third id beside {@link fieldFor} and {@link valueFor}, for the reason
   * `valueFor`'s own doc gives: the state is proved by THIS id being present
   * while the editable one is absent, which a shared id could not express.
   */
  unbackedFieldFor: (ruleId: string, fieldId: string): string =>
    `FIN_TARGETS.UNBACKED_FIELD.${ruleId}.${fieldId}`,
  /**
   * The disabled control INSIDE a greyed box (SE-8454), keyed by rule id
   * then field id.
   *
   * A second id on the same field exists for one job: letting a test assert
   * the control is actually disabled. {@link unbackedFieldFor} sits on the
   * wrapper because a disabled field leaves the accessibility tree — which
   * is the very reason a test cannot reach the control to check it without
   * naming it here. Without this, dropping `isDisabled` would leave every
   * assertion green while the reader typed into a box going nowhere.
   */
  unbackedInputFor: (ruleId: string, fieldId: string): string =>
    `FIN_TARGETS.UNBACKED_INPUT.${ruleId}.${fieldId}`,
};

/**
 * Actionable-insights feed (SE-7007).
 *
 * A namespace of its own rather than more keys on `TEST_ID_FIN_COMPLIANCE`:
 * the feed is a separate content block with its own empty state and its own
 * removal flow, and an id here should not read as "part of the KPI grid".
 */
export const TEST_ID_FIN_INSIGHTS = {
  FEED: "FIN_INSIGHTS.FEED",
  HEADER: "FIN_INSIGHTS.HEADER",
  COUNT: "FIN_INSIGHTS.COUNT",
  COMING_SOON: "FIN_INSIGHTS.COMING_SOON",
  COLLAPSE_TOGGLE: "FIN_INSIGHTS.COLLAPSE_TOGGLE",
  LIST: "FIN_INSIGHTS.LIST",
  EMPTY_STATE: "FIN_INSIGHTS.EMPTY_STATE",
  SHOW_DISMISSED: "FIN_INSIGHTS.SHOW_DISMISSED",
  HIDE_DISMISSED: "FIN_INSIGHTS.HIDE_DISMISSED",
  UNDO_BAR: "FIN_INSIGHTS.UNDO_BAR",
  UNDO_TITLE: "FIN_INSIGHTS.UNDO_TITLE",
  UNDO_DETAIL: "FIN_INSIGHTS.UNDO_DETAIL",
  UNDO: "FIN_INSIGHTS.UNDO",
  cardFor: (insightId: string): string => `FIN_INSIGHTS.CARD.${insightId}`,
  headlineFor: (insightId: string): string =>
    `FIN_INSIGHTS.HEADLINE.${insightId}`,
  sublineFor: (insightId: string): string =>
    `FIN_INSIGHTS.SUBLINE.${insightId}`,
  expandFor: (insightId: string): string => `FIN_INSIGHTS.EXPAND.${insightId}`,
  reasoningFor: (insightId: string): string =>
    `FIN_INSIGHTS.REASONING.${insightId}`,
  reasonFor: (insightId: string, reasonId: string): string =>
    `FIN_INSIGHTS.REASON.${insightId}.${reasonId}`,
  actionsFor: (insightId: string): string =>
    `FIN_INSIGHTS.ACTIONS.${insightId}`,
  actionFor: (insightId: string, actionId: string): string =>
    `FIN_INSIGHTS.ACTION.${insightId}.${actionId}`,
  menuTriggerFor: (insightId: string): string =>
    `FIN_INSIGHTS.MENU_TRIGGER.${insightId}`,
  // The open overflow panel. It is portalled out of the card on web, so this
  // is the only handle a test has on "the menu is open" as distinct from "a
  // menu item exists somewhere on the page".
  menuPanelFor: (insightId: string): string =>
    `FIN_INSIGHTS.MENU_PANEL.${insightId}`,
  dismissFor: (insightId: string): string =>
    `FIN_INSIGHTS.DISMISS.${insightId}`,
  muteFor: (insightId: string): string => `FIN_INSIGHTS.MUTE.${insightId}`,
  // The peek's own reversal (SE-7849 continued): a dismissed/muted insight's
  // menu swaps DISMISS/MUTE above for these instead — never both at once for
  // the same row and the same suppression.
  undismissFor: (insightId: string): string =>
    `FIN_INSIGHTS.UNDISMISS.${insightId}`,
  unmuteFor: (insightId: string): string => `FIN_INSIGHTS.UNMUTE.${insightId}`,
};

/**
 * The three-column compliance layer board (SE-7006).
 *
 * A namespace of its own rather than more keys on `TEST_ID_FIN_COMPLIANCE`:
 * the board is the content the page shell's `LAYERS_SLOT` was declared to
 * hold, and an id here should not read as "part of the KPI grid". The slot
 * keeps its own id — it is the composition point, this is what fills it — so
 * a spec can assert the section exists separately from asserting what is in
 * it.
 *
 * ## A column id exists for all three layers, always
 *
 * `columnFor` is present on every render whether or not the layer holds
 * rules, because a layer that disappears when it is empty is
 * indistinguishable from a layer nobody configured and from a bug. An empty
 * column carries `columnNoticeFor` INSTEAD of rule cards, so
 * `getByTestId(columnFor(layer))` is a positive control that pairs with every
 * `queryAllByTestId(ruleFor(id))` absence assertion.
 *
 * ## The rule ids are the DEDUPLICATION assertion
 *
 * `ruleFor` is keyed by the server's `ruleId`, and the board admits a rule
 * exactly once across all three columns. A spec proves that by querying ALL
 * elements matching one rule's id and asserting the count is 1 — which only
 * means anything because the id is per-rule rather than per-column.
 *
 * `consequenceFor` (SE-8611) is present only when the server authored text
 * for this rule — mirroring `basedOnFor` below, a spec proves the section is
 * absent (not blank) for a rule with none by querying rather than getting.
 */
export const TEST_ID_FIN_LAYERS = {
  /** The three-column container. */
  BOARD: "FIN_LAYERS.BOARD",
  /**
   * What a FAILED read renders instead of the board.
   *
   * Distinct from an empty column's notice, and deliberately so: an empty
   * column reports that nothing is configured, which is a fact about the club.
   * This reports that the check did not run, which is a fact about the read.
   * Rendering nothing on error would make the two indistinguishable.
   */
  ERROR: "FIN_LAYERS.ERROR",
  /**
   * What a WITHHELD metric registry renders instead of the board (SE-7627).
   *
   * A third id rather than a reuse of ERROR, because it reports a third thing.
   * ERROR says the check did not run. An empty column says the club configured
   * nothing. This says the check ran, answered, and the answer is not this
   * reader's to see — a fact about the VIEWER, which neither of the other two
   * can express. Before SE-7627 this case rendered no element at all, so a
   * spec could not tell it from a board still loading.
   */
  METRICS_WITHHELD: "FIN_LAYERS.METRICS_WITHHELD",
  /** One layer column, keyed by `ComplianceRuleLayer`. */
  columnFor: (layer: string): string => `FIN_LAYERS.COLUMN.${layer}`,
  /** A column's heading. */
  columnTitleFor: (layer: string): string => `FIN_LAYERS.COLUMN_TITLE.${layer}`,
  /**
   * What an EMPTY column says instead of vanishing.
   *
   * Present only when the column holds no rules, so its absence is itself an
   * assertion that the layer is populated.
   */
  columnNoticeFor: (layer: string): string =>
    `FIN_LAYERS.COLUMN_NOTICE.${layer}`,
  /**
   * The notice's CTA.
   *
   * Absent when the notice has no remedy — a club not enrolled in a domestic
   * regime has nothing to configure — which is why this is a separate id from
   * the notice rather than a child of it.
   */
  columnNoticeActionFor: (layer: string): string =>
    `FIN_LAYERS.COLUMN_NOTICE_ACTION.${layer}`,
  /** One rule card, keyed by rule id. */
  ruleFor: (ruleId: string): string => `FIN_LAYERS.RULE.${ruleId}`,
  /** A rule's status dot. */
  ruleDotFor: (ruleId: string): string => `FIN_LAYERS.RULE_DOT.${ruleId}`,
  /** A rule's name, on the card's top row. */
  ruleNameFor: (ruleId: string): string => `FIN_LAYERS.RULE_NAME.${ruleId}`,
  /** A rule's one-line status sentence. */
  ruleSummaryFor: (ruleId: string): string =>
    `FIN_LAYERS.RULE_SUMMARY.${ruleId}`,
  /** The "Understand" disclosure trigger. */
  expandFor: (ruleId: string): string => `FIN_LAYERS.EXPAND.${ruleId}`,
  /**
   * The expanded detail panel.
   *
   * Mounted only while open — not hidden — so a spec scoping a lookup to this
   * id cannot pass against a collapsed card.
   */
  detailFor: (ruleId: string): string => `FIN_LAYERS.DETAIL.${ruleId}`,
  /** The COMPUTED FIX sentence, carrying the server's number. */
  computedFixFor: (ruleId: string): string =>
    `FIN_LAYERS.COMPUTED_FIX.${ruleId}`,
  /**
   * Why there is no computed fix.
   *
   * Exactly one of this and `computedFixFor` is present, never both and never
   * neither — so a spec can prove the panel always accounts for the remedy.
   */
  computedFixNoteFor: (ruleId: string): string =>
    `FIN_LAYERS.COMPUTED_FIX_NOTE.${ruleId}`,
  /** The OBJECTIVES reading list — the INTERNAL layer's block. */
  readingsFor: (ruleId: string): string => `FIN_LAYERS.READINGS.${ruleId}`,
  /** One `label / value` reading row. */
  readingRowFor: (ruleId: string, readingId: string): string =>
    `FIN_LAYERS.READING.${ruleId}.${readingId}`,
  /** The DRIVERS list — the regime layers' block. */
  driversFor: (ruleId: string): string => `FIN_LAYERS.DRIVERS.${ruleId}`,
  /** One driver row, keyed by the contributing subject. */
  driverRowFor: (ruleId: string, subjectId: string): string =>
    `FIN_LAYERS.DRIVER.${ruleId}.${subjectId}`,
  /** One action button under a rule's detail. */
  actionFor: (ruleId: string, actionId: string): string =>
    `FIN_LAYERS.ACTION.${ruleId}.${actionId}`,
  /**
   * The "Based on" line (SE-8517) — present only for a squad-cost-control
   * rule, where it states the club revenue figure and the regime the rule is
   * measured under.
   */
  basedOnFor: (ruleId: string): string => `FIN_LAYERS.BASED_ON.${ruleId}`,
  /**
   * The CONSEQUENCE line (SE-8611) — present only when the server authored
   * text for this rule; absent (not blank) otherwise.
   */
  consequenceFor: (ruleId: string): string =>
    `FIN_LAYERS.CONSEQUENCE.${ruleId}`,
};

/**
 * Home create-note quick action (SE-6845).
 *
 * `resultFor` mirrors the `FIN_INSIGHTS` id-suffix convention so a spec can
 * target one specific player's row, while the shared prefix lets it target
 * "whichever player came back first" with a `^=` selector.
 */
export const TEST_ID_CREATE_NOTE_QUICK_ACTION = {
  TRIGGER: "home-create-note-action",
  INPUT: "home-create-note-search-input",
  RESULT_PREFIX: "home-create-note-result-",
  LOADING: "home-create-note-loading",
  ERROR: "home-create-note-error",
  EMPTY: "home-create-note-empty",
  PROMPT: "home-create-note-prompt",
  resultFor: (playerId: string): string =>
    `home-create-note-result-${playerId}`,
};

/**
 * PSR / FFP breakdown and per-year entry (SE-7011).
 *
 * A namespace of its own rather than more keys on `TEST_ID_FIN_COMPLIANCE`:
 * the breakdown is a drill-through surface with its own states, and an id here
 * should not read as "part of the KPI grid". It follows `FIN_INSIGHTS`'s
 * id-suffix convention so a spec can target one reporting year's row.
 *
 * `ENTRY` and everything under it are absent from the DOM for a viewer without
 * entry rights — not hidden, absent — because the payload carries no entry
 * model at all. A spec asserting `toHaveCount(0)` on those ids is therefore
 * asserting the payload, not a CSS rule, which is why each such assertion is
 * paired with a positive taken from the same render.
 */
export const TEST_ID_FIN_PSR = {
  PANEL: "FIN_PSR.PANEL",
  TITLE: "FIN_PSR.TITLE",
  WINDOW: "FIN_PSR.WINDOW",
  STATUS: "FIN_PSR.STATUS",
  TOTAL: "FIN_PSR.TOTAL",
  TARGET: "FIN_PSR.TARGET",
  HEADROOM: "FIN_PSR.HEADROOM",
  ROWS: "FIN_PSR.ROWS",
  SCENARIO_DELTA: "FIN_PSR.SCENARIO_DELTA",
  // SE-8452. The scenario's real cost delta, implied by Squad Cost Ratio's
  // own reading (`adapters/psrSource.ts`) — present whenever it could be
  // computed.
  SCENARIO_DELTA_VALUE: "FIN_PSR.SCENARIO_DELTA_VALUE",
  // SE-8452 (renamed from SE-8397's `SCENARIO_DELTA_COMING_SOON`). Shown
  // only in the genuine "could not be computed" case now — a club not
  // enrolled in Squad Cost Ratio, or with no adjusted revenue entered —
  // never as a blanket placeholder for a delta nobody has evaluated.
  SCENARIO_DELTA_UNAVAILABLE: "FIN_PSR.SCENARIO_DELTA_UNAVAILABLE",
  CLOSE: "FIN_PSR.CLOSE",
  ENTRY: "FIN_PSR.ENTRY",
  ENTRY_HINT: "FIN_PSR.ENTRY_HINT",
  ENTRY_SAVE: "FIN_PSR.ENTRY_SAVE",
  ENTRY_SAVE_BLOCKED_NOTE: "FIN_PSR.ENTRY_SAVE_BLOCKED_NOTE",
  /**
   * SE-7747: a decorative iOS-only spacer below the Save row that gives the
   * page's ScrollView enough content height to scroll the LAST entry field
   * (e.g. the current-year forecast) clear of the software keyboard. See
   * `PsrBreakdownView`'s `renderEntry` for why this exists.
   */
  ENTRY_KEYBOARD_CLEARANCE: "FIN_PSR.ENTRY_KEYBOARD_CLEARANCE",
  rowFor: (periodId: string): string => `FIN_PSR.ROW.${periodId}`,
  rowValueFor: (periodId: string): string => `FIN_PSR.ROW_VALUE.${periodId}`,
  rowNoteFor: (periodId: string): string => `FIN_PSR.ROW_NOTE.${periodId}`,
  entryFieldFor: (periodId: string): string =>
    `FIN_PSR.ENTRY_FIELD.${periodId}`,
  entryDateFor: (periodId: string): string => `FIN_PSR.ENTRY_DATE.${periodId}`,
};

/**
 * The per-KPI Edit dialog and the KPI drill-through (SE-7010).
 *
 * A namespace of its own rather than more keys on `TEST_ID_FIN_COMPLIANCE`:
 * these are overlays raised FROM the grid, not parts of it, and both render
 * through a portal — so an id here is the only handle a test has on "the dialog
 * is open" as distinct from "some text exists somewhere on the page".
 *
 * The field ids are the ones `domain/editKpiForm` declares (`target`, `warn`,
 * `basis`, `homegrown-minimum`), never the KPI id: one dialog serves all six
 * KPIs, so a per-KPI id here would imply six dialogs that do not exist.
 */
export const TEST_ID_FIN_KPI_DIALOG = {
  EDIT_DIALOG: "FIN_KPI_DIALOG.EDIT",
  EDIT_TITLE: "FIN_KPI_DIALOG.EDIT_TITLE",
  EDIT_CLOSE: "FIN_KPI_DIALOG.EDIT_CLOSE",
  EDIT_TREND_SWITCH: "FIN_KPI_DIALOG.EDIT_TREND_SWITCH",
  EDIT_REMOVE: "FIN_KPI_DIALOG.EDIT_REMOVE",
  EDIT_CANCEL: "FIN_KPI_DIALOG.EDIT_CANCEL",
  EDIT_SAVE: "FIN_KPI_DIALOG.EDIT_SAVE",
  /**
   * The backend's own rejection text, shown when Save reaches the server and
   * does not land (SE-7578 gap 1) — e.g. a target/warn ordering rule. Absent
   * whenever there is nothing to show, same convention as
   * `TEST_ID_FIN_OBJECTIVES.violationFor`.
   */
  EDIT_SAVE_ERROR: "FIN_KPI_DIALOG.EDIT_SAVE_ERROR",
  DRILL_DIALOG: "FIN_KPI_DIALOG.DRILL",
  DRILL_TITLE: "FIN_KPI_DIALOG.DRILL_TITLE",
  DRILL_SUBTITLE: "FIN_KPI_DIALOG.DRILL_SUBTITLE",
  DRILL_LIST: "FIN_KPI_DIALOG.DRILL_LIST",
  // SE-8397. Present instead of `DRILL_LIST` for a KPI whose drivers are not
  // real yet (Squad Cost Ratio) — never alongside it.
  /** The header's ✕. */
  DRILL_CLOSE: "FIN_KPI_DIALOG.DRILL_CLOSE",
  /** The footer's "Close" button — a second, separately-findable dismissal. */
  DRILL_CLOSE_ACTION: "FIN_KPI_DIALOG.DRILL_CLOSE_ACTION",
  /**
   * The row holding the KPI's entry fields.
   *
   * Findable because the row's AXIS is a claim the responsive work makes
   * (SE-7012): two boxes side by side at desktop, stacked at mobile. Without an
   * identity of its own that fork can only be inferred from a child's position,
   * which is not a thing a test can read.
   */
  EDIT_FIELDS_ROW: "FIN_KPI_DIALOG.EDIT_FIELDS_ROW",
  /** An editable entry box, keyed by field id. */
  editFieldFor: (fieldId: string): string =>
    `FIN_KPI_DIALOG.EDIT_FIELD.${fieldId}`,
  /**
   * The same value rendered as TEXT for a reader who may not edit (AC3).
   *
   * A separate id from the editable box on purpose: "the value is read-only" is
   * proved by this id being present while `editFieldFor` is absent, which a
   * shared id could not express.
   */
  editValueFor: (fieldId: string): string =>
    `FIN_KPI_DIALOG.EDIT_VALUE.${fieldId}`,
  /**
   * One driver row in the drill-through, keyed by player id.
   *
   * There is deliberately no id for a driver's RANK. "Top contributions" is a
   * ranking expressed by the order of these rows and nothing else — the frame
   * draws no rank marker — so rank is asserted by reading the rows in order,
   * not by looking up a badge that would have to be invented to be findable.
   */
  drillRowFor: (playerId: string): string =>
    `FIN_KPI_DIALOG.DRILL_ROW.${playerId}`,
  /** A driver's contribution, keyed by player id. */
  drillValueFor: (playerId: string): string =>
    `FIN_KPI_DIALOG.DRILL_VALUE.${playerId}`,
};

/**
 * The Club objectives modal and the toolbar button that raises it (SE-7004).
 *
 * Its own namespace rather than keys on `TEST_ID_FIN_COMPLIANCE`, for the
 * reason `TEST_ID_FIN_KPI_DIALOG` gives: this is an overlay raised FROM a
 * surface, rendered through a portal, so an id here is the only handle a test
 * has on "the modal is open" as distinct from "some text exists somewhere".
 *
 * The three access states each get an id of their own — `LIST`, `EMPTY`,
 * `NOT_PERMITTED` — instead of sharing one and asserting on its text. They are
 * genuinely different outcomes: an empty list says "this club has authored
 * none" and a forbidden read says "you may not be told either way", and a
 * shared id would let a test pass while the surface leaked the first answer to
 * a viewer entitled only to the second.
 */
export const TEST_ID_FIN_OBJECTIVES = {
  /** The Squad Planning toolbar button that opens the modal. */
  BUTTON: "FIN_OBJECTIVES.BUTTON",
  /** The modal itself. */
  MODAL: "FIN_OBJECTIVES.MODAL",
  /** The modal's heading. */
  TITLE: "FIN_OBJECTIVES.TITLE",
  /** The list of authored Objectives — the `configured` state. */
  LIST: "FIN_OBJECTIVES.LIST",
  /** The permitted-but-nothing-authored state. */
  EMPTY: "FIN_OBJECTIVES.EMPTY",
  /** The tier-forbidden state. */
  NOT_PERMITTED: "FIN_OBJECTIVES.NOT_PERMITTED",
  /**
   * The permitted-but-registry-withheld state (SE-7627).
   *
   * Its own id rather than `EMPTY`, because a spec that could not tell the two
   * apart could not detect the defect: an assertion satisfied by an empty
   * registry is satisfied by a withheld one too, which is the whole bug. These
   * two ids are mutually exclusive by construction — asserting this one is
   * present AND `EMPTY` absent is what distinguishes "your access level" from
   * "this club has none".
   */
  METRICS_WITHHELD: "FIN_OBJECTIVES.METRICS_WITHHELD",
  /** Shown while the reads are in flight, BEFORE `EMPTY` may be drawn. */
  LOADING: "FIN_OBJECTIVES.LOADING",
  /** Shown when a read failed. */
  ERROR: "FIN_OBJECTIVES.ERROR",
  /** The standing "Objectives inform, they never block" note. */
  INFO_NOTE: "FIN_OBJECTIVES.INFO_NOTE",
  /** The list's "Add objective" row. */
  ADD: "FIN_OBJECTIVES.ADD",
  /** The list's "Close" button. */
  CLOSE: "FIN_OBJECTIVES.CLOSE",
  /**
   * The backend's own rejection when a Remove reaches the server and does not
   * land (SE-8201) — e.g. an objective with snapshot history recorded against
   * it. Absent whenever there is nothing to show, same convention as
   * `TEST_ID_FIN_KPI_DIALOG.EDIT_SAVE_ERROR`.
   */
  REMOVE_ERROR: "FIN_OBJECTIVES.REMOVE_ERROR",
  /**
   * The "Remove this objective?" confirmation raised by the bin icon
   * (SE-8758) — the delete no longer fires on the click itself. Shared by
   * both the enabled and the disabled row's remove affordance, since both
   * flow through the same `removeObjective` seam.
   */
  REMOVE_CONFIRM: "FIN_OBJECTIVES.REMOVE_CONFIRM",
  /**
   * The list's "Show disabled" link (SE-8230) — mirrors
   * `TEST_ID_FIN_INSIGHTS.SHOW_DISMISSED`. Present only while at least one
   * disabled Objective exists and the peek is not already open.
   */
  SHOW_DISABLED: "FIN_OBJECTIVES.SHOW_DISABLED",
  /** The list's "Hide disabled" link — the peek's own reverse. */
  HIDE_DISABLED: "FIN_OBJECTIVES.HIDE_DISABLED",
  /** The disabled Objectives revealed by "Show disabled". */
  DISABLED_LIST: "FIN_OBJECTIVES.DISABLED_LIST",

  /** The Objective editor pane. */
  EDIT_PANE: "FIN_OBJECTIVES.EDIT_PANE",
  /** The editor's `Show KPI` toggle. Absent for a squad objective. */
  SHOW_KPI: "FIN_OBJECTIVES.SHOW_KPI",
  /**
   * The editor's `Enabled` toggle (SE-8230). Absent while CREATING — a new
   * objective is always created enabled; there is nothing yet to retire.
   */
  IS_ENABLED: "FIN_OBJECTIVES.IS_ENABLED",
  /** The editor's dismissal. */
  CANCEL: "FIN_OBJECTIVES.CANCEL",
  /** The editor's commit. */
  SAVE: "FIN_OBJECTIVES.SAVE",
  /** The METRIC picker's last row, which opens the builder. */
  ADD_METRIC: "FIN_OBJECTIVES.ADD_METRIC",

  /** The new-metric builder pane. */
  METRIC_PANE: "FIN_OBJECTIVES.METRIC_PANE",
  /** The builder's commit. */
  CREATE_METRIC: "FIN_OBJECTIVES.CREATE_METRIC",

  /** One Objective's row, keyed by rule id. */
  rowFor: (ruleId: string): string => `FIN_OBJECTIVES.ROW.${ruleId}`,
  /** One row's rendered target, e.g. `≤ 70%`. */
  rowTargetFor: (ruleId: string): string =>
    `FIN_OBJECTIVES.ROW_TARGET.${ruleId}`,
  /** One row's FINANCIAL tag. Present only on a financial Objective. */
  rowTagFor: (ruleId: string): string => `FIN_OBJECTIVES.ROW_TAG.${ruleId}`,
  /** One row's DISABLED tag (SE-8230). Present only on a disabled row. */
  rowDisabledTagFor: (ruleId: string): string =>
    `FIN_OBJECTIVES.ROW_DISABLED_TAG.${ruleId}`,
  /** One row's edit affordance. */
  rowEditFor: (ruleId: string): string => `FIN_OBJECTIVES.ROW_EDIT.${ruleId}`,
  /** One row's remove affordance. */
  rowRemoveFor: (ruleId: string): string =>
    `FIN_OBJECTIVES.ROW_REMOVE.${ruleId}`,

  /**
   * A field's control — the box a person presses or types into, keyed by the
   * field id (`metric`, `direction`, `timeframe`, `target`, `amber`, …).
   */
  fieldFor: (fieldId: string): string => `FIN_OBJECTIVES.FIELD.${fieldId}`,
  /** One row inside an open picker, keyed by field id and the value it picks. */
  optionFor: (fieldId: string, value: string): string =>
    `FIN_OBJECTIVES.OPTION.${fieldId}.${value}`,
  /**
   * The message a rejected draft puts under the field at fault.
   *
   * Keyed by the violation's `at`, which is the domain's own field name — so a
   * test asserts the message landed on the field the validator blamed rather
   * than merely somewhere on the pane.
   */
  violationFor: (at: string): string => `FIN_OBJECTIVES.VIOLATION.${at}`,
  /**
   * The helper note a locked field shows in place of a violation — e.g. why
   * METRIC cannot be changed once an Objective already exists (SE-7880).
   */
  helperFor: (fieldId: string): string => `FIN_OBJECTIVES.HELPER.${fieldId}`,

  /** One operand's WHERE filter block, keyed by role (`of` / `over`). */
  whereFor: (role: string): string => `FIN_OBJECTIVES.WHERE.${role}`,
  /** The call to action that adds an operand's WHERE filter. */
  whereAddFor: (role: string): string => `FIN_OBJECTIVES.WHERE_ADD.${role}`,
  /** The ✕ that removes an operand's WHERE filter. */
  whereClearFor: (role: string): string => `FIN_OBJECTIVES.WHERE_CLEAR.${role}`,
  /** One chip in an operand's WHERE filter (`attribute` / `comparator` / `value`). */
  whereChipFor: (role: string, part: string): string =>
    `FIN_OBJECTIVES.WHERE_CHIP.${role}.${part}`,
};

/**
 * The app-wide connectivity warning (SE-6833, SE-6983).
 *
 * The dismiss control is named because dismissing it is a behaviour with
 * consequences beyond the modal: it suppresses the warning for the session
 * while the connectivity probe deliberately keeps running, which is what lets
 * the offline drain notice signal returning.
 */
export const TEST_ID_NETWORK_STATUS = {
  DISMISS: "NETWORK_STATUS.DISMISS",
};

/**
 * The knowledge-graph chat transcript (SE-8049).
 *
 * The row id is minted from this stem rather than by extending the list's own
 * `testID` prop. Extending it would put every row INSIDE the list's namespace,
 * so a count taken against the list prefix would tally the list plus one per
 * row — harmless for a zero-count assertion and wrong for every comparison.
 * `messageFor` makes each row a SIBLING of the list instead, which keeps the
 * list's prefix counting exactly the one element it names.
 */
export const TEST_ID_MESSAGE_LIST = {
  /** One message row, keyed by the message's own id. */
  messageFor: (messageId: string): string =>
    `MESSAGE_LIST.MESSAGE.${messageId}`,
};
// SE-8356 (PV16 / PRD-5.5): sharing a scenario — the Share entry on the
// scenario Pitch left panel, the invitee's "Shared by" line and the WHAT THEY
// CAN SEE section (one Financials switch) the scenario Share dialog adds to
// the shared PRD-6.1 model. The invite row and WHO HAS ACCESS rows keep the
// `TEST_ID_WATCHLIST_SHARE` ids because they are the same molecules.
export const TEST_ID_SCENARIO_SHARE = {
  /** Share button beside the ownership badge (owner only). */
  OPEN_BUTTON: "SCENARIO_SHARE.OPEN_BUTTON",
  /** "Shared with you by {owner} · Can view/edit" on the Pitch left panel. */
  SHARED_BY: "SCENARIO_SHARE.SHARED_BY",
  /** The WHAT THEY CAN SEE block. */
  FINANCIALS_SECTION: "SCENARIO_SHARE.FINANCIALS_SECTION",
  /** The Financials switch itself. */
  FINANCIALS_SWITCH: "SCENARIO_SHARE.FINANCIALS_SWITCH",
  /** Refusal copy under the switch (revision conflict, forbidden, …). */
  FINANCIALS_NOTICE: "SCENARIO_SHARE.FINANCIALS_NOTICE",
  /** The PRD-5.5 editors / owner note. */
  NOTE: "SCENARIO_SHARE.NOTE",
  /** "Scouts can't access scenarios" under the invite row. */
  INVITE_CAPTION: "SCENARIO_SHARE.INVITE_CAPTION",
};

// PV18 (SE-8360, PRD-7.6): the watchlist table's Export ▾ menu (CSV · PDF)
// and its operation states — busy control while pages load, the written
// filename on completion, the failure line with Retry.
export const TEST_ID_WATCHLIST_EXPORT = {
  /** The Export ▾ trigger. */
  TRIGGER: "WATCHLIST_EXPORT.TRIGGER",
  /** Disabled control reading "Exporting… n of N" while the run is in flight. */
  BUSY: "WATCHLIST_EXPORT.BUSY",
  /** SE-8535: screen-reader-only live progress when the busy control is an icon. */
  PROGRESS: "WATCHLIST_EXPORT.PROGRESS",
  /** "Exported {filename}" once the file is written. */
  DONE: "WATCHLIST_EXPORT.DONE",
  /** "Export failed: {message}". */
  FAILED: "WATCHLIST_EXPORT.FAILED",
  /** Retry after a failure — restarts from page 1. */
  RETRY: "WATCHLIST_EXPORT.RETRY",
  /** Cancel beside the busy control — stops between pages, writes no file. */
  CANCEL: "WATCHLIST_EXPORT.CANCEL",
  /** One menu row per format ("csv" | "pdf"). */
  itemFor: (format: string): string => `WATCHLIST_EXPORT.ITEM.${format}`,
};

/**
 * The two save surfaces the Pitch and the Table both mount (SE-8389): the
 * toolbar status line and the D17 conflict banner. On the Pitch route the
 * Table stays mounted (hidden) beside the Pitch, so each surface carries its
 * OWN stem per host — a bare `getByTestId("pitch-save-banner")` must resolve
 * to the Pitch's banner and nothing else (CI run 35429981013 measured 2).
 * Child ids are minted here, never by extending a `testID` prop (SE-8049).
 */
export const TEST_ID_PITCH_SAVE = {
  pitch: {
    status: {
      ROOT: "pitch-save-status",
      TEXT: "pitch-save-status-text",
      ACTION: "pitch-save-status-action",
    },
    banner: {
      ROOT: "pitch-save-banner",
      MESSAGE: "pitch-save-banner-message",
      RELOAD: "pitch-save-banner-reload",
      REAPPLY: "pitch-save-banner-reapply",
      RETRY: "pitch-save-banner-retry",
      DISCARD: "pitch-save-banner-discard",
      DISMISS: "pitch-save-banner-dismiss",
    },
  },
  table: {
    status: {
      ROOT: "watchlist-table-save-status",
      TEXT: "watchlist-table-save-status-text",
      ACTION: "watchlist-table-save-status-action",
    },
    banner: {
      ROOT: "watchlist-table-save-banner",
      MESSAGE: "watchlist-table-save-banner-message",
      RELOAD: "watchlist-table-save-banner-reload",
      REAPPLY: "watchlist-table-save-banner-reapply",
      RETRY: "watchlist-table-save-banner-retry",
      DISCARD: "watchlist-table-save-banner-discard",
      DISMISS: "watchlist-table-save-banner-dismiss",
    },
  },
} as const;

/** Which host mounts a save surface; selects its test-identity stem. */
export type PitchSaveSurface = keyof typeof TEST_ID_PITCH_SAVE;
